import { fabric } from 'fabric';
import _ from 'lodash';
import moment from 'moment';
import { GAIN_VALUES, SPEED_VALUES } from '@/shared/config';
import { getDataRangeWithin } from '@/shared/lib/timeRange';
import { isSameBeats } from '@/shared/lib';
import { convertToMomentJs } from '@/shared/lib/aiUtils'; // Updated utility function
import { toastrError } from '@/shared/lib/toastNotification';
import {
  ANNOTATION_TYPE,
  DEFAULT_ECG_VIEWER_CONFIG,
  ECG_VIEWER_CONFIG_STORE,
  MOUSE_CURSOR,
  TYPE_MODE,
} from '@/shared/ui';
import {
  convertBeatTypeNumberToText,
  getClosestChannelIndex,
  isDeleteBeatMode,
  median,
  optimizeSVG,
  reCalculateConfig,
} from '@/shared/ui/components/FabricJS/Helper';
import { Highpass, Lowpass } from '@/shared/ui/components/FabricJS/Helper/lowHighPassFilter';
import { getBeatMarkersArr } from '@/shared/ui/components/FabricJS/Helper/pantompkin';
import { getPQSTObject } from '@/shared/ui/components/FabricJS/Helper/pqrstCalculator';
import { Annotations } from '@/shared/ui/components/FabricJS/View/Annotations';
import { BeatMarkers } from '@/shared/ui/components/FabricJS/View/Annotations/Components/beatMarkers';
import { TitleChart } from '@/shared/ui/components/FabricJS/View/Annotations/Components/titleChart';
import { Scroller } from '@/shared/ui/components/FabricJS/View/Scroller';
import { EcgView } from '@/shared/ui/components/FabricJS/View/ecgView';
import { Grid } from '@/shared/ui/components/FabricJS/View/grid';

// https://github.com/fabricjs/fabric.js/issues/6588
// const initHiddenTextarea = function () {
//   this.hiddenTextarea = fabric.document.createElement('textarea');
//   this.hiddenTextarea.setAttribute('autocapitalize', 'off');
//   this.hiddenTextarea.setAttribute('autocorrect', 'off');
//   this.hiddenTextarea.setAttribute('autocomplete', 'off');
//   this.hiddenTextarea.setAttribute('spellcheck', 'false');
//   this.hiddenTextarea.setAttribute('data-fabric-hiddentextarea', '');
//   this.hiddenTextarea.setAttribute('wrap', 'off');
//   const style = this._calcTextareaPosition();
//   // line-height: 1px; was removed from the style to fix this:
//   // https://bugs.chromium.org/p/chromium/issues/detail?id=870966
//   this.hiddenTextarea.style.cssText = `position: absolute; top: ${style.top
//   }; left: ${style.left}; z-index: -999; opacity: 0; width: 1px; height: 1px; font-size: 1px;`
//     + ` paddingｰtop: ${style.fontSize};`;
//   if (this.hiddenTextareaContainer) {
//     this.hiddenTextareaContainer.appendChild(this.hiddenTextarea);
//   } else {
//     fabric.document.body.appendChild(this.hiddenTextarea);
//   }
//   fabric.util.addListener(this.hiddenTextarea, 'keydown', this.onKeyDown.bind(this));
//   fabric.util.addListener(this.hiddenTextarea, 'keyup', this.onKeyUp.bind(this));
//   fabric.util.addListener(this.hiddenTextarea, 'input', this.onInput.bind(this));
//   fabric.util.addListener(this.hiddenTextarea, 'copy', this.copy.bind(this));
//   fabric.util.addListener(this.hiddenTextarea, 'cut', this.copy.bind(this));
//   fabric.util.addListener(this.hiddenTextarea, 'paste', this.paste.bind(this));
//   fabric.util.addListener(this.hiddenTextarea, 'compositionstart', this.onCompositionStart.bind(this));
//   fabric.util.addListener(this.hiddenTextarea, 'compositionupdate', this.onCompositionUpdate.bind(this));
//   fabric.util.addListener(this.hiddenTextarea, 'compositionend', this.onCompositionEnd.bind(this));
//   if (!this._clickHandlerInitialized && this.canvas) {
//     fabric.util.addListener(this.canvas.upperCanvasEl, 'click', this.onClick.bind(this));
//     this._clickHandlerInitialized = true;
//   }
// };

// fabric.util.object.extend(fabric.IText.prototype, { initHiddenTextarea });
fabric.Object.prototype.objectCaching = false;
// multiple event listener prevent ITEXT receive key (event: any) => need overwrite and call stop propagation
fabric.IText.prototype.onKeyDown = (function (onKeyDown: any) {
  return function (e: any) {
    e.stopImmediatePropagation();
    onKeyDown.call(this, e);
  };
})(fabric.IText.prototype.onKeyDown);

// eslint-disable-next-line import/no-mutable-exports
let AIECGChart: any;

(function () {
  const ACTION_TYPE = {
    AR_BEAT_MARKER: 'isARingBeatMarker',
    ZOOMING: 'isZooming',
  };
  const MOUSE_EVENT_TYPE = {
    MOUSE_WHEEL: 'mousewheel',
    MOUSE_MOVE: 'mousemove',
  };
  const textChannel = ['CH1', 'CH2', 'CH3'];
  const backgroundColor = '#fafafa';

  AIECGChart = function ({
    rawDataFromNetwork = [[], [], []],
    config,
    beatMarkerArr = [],
    hesBeatStatus = [],
    annotations,
    functionObject,
    isDisplayScrollbar,
    hiddenChannels = [0, 1, 2],
    events,
    missingSampleLength,
    aiData,
    isAppliedAI,
    isShowVES,
    isShowSVES,
    diagnosticLead,
    calculateBeatMarkerChannel,
    idComponents = {
      canvasBackground: 'my-canvas1-1',
      exportGridCanvas: 'my-canvas1-2',
      canvasChart: 'my-canvas2',
      canvasObject: 'my-canvas3',
      canvasScroller: 'my-canvas4',
      tab: 'my-tab',
    },
    timeObject,
    ecgDataMap,
  }: any) {
    this.timeObject = timeObject;
    this.ecgDataMap = ecgDataMap;
    this.offsetX = 0;
    this.isAppliedAI = isAppliedAI;
    this.isShowVES = isShowVES;
    this.isShowSVES = isShowSVES;
    this.diagnosticLead = diagnosticLead;
    this.calculateBeatMarkerChannel = calculateBeatMarkerChannel;
    this.isInViewAllMode = false;
    this.isDisplayScrollbar = isDisplayScrollbar;
    this.assignFunction(functionObject);
    this.bindEventListener();
    this.initValue(
      rawDataFromNetwork,
      config,
      beatMarkerArr,
      hesBeatStatus,
      hiddenChannels,
      missingSampleLength,
    );
    this.aiData = aiData;
    // TODO: Testing with dummy VES beat markers data
    // _.assign(this.aiData, { beats: [50, 100, ...aiData.beats] });
    // _.assign(this.aiData, { hesBeatStatus: [60, 60, ...aiData.hesBeatStatus] });
    // _.assign(this.aiData, { hesEventStatus: [0, 0, ...aiData.hesEventStatus] });
    this.rawDataFromNetwork = [...rawDataFromNetwork];
    this.rawDataZoom = [...rawDataFromNetwork];
    this.idComponents = idComponents;

    this.isInZoomMode = ECG_VIEWER_CONFIG_STORE.isInZoomMode;
    this.zoomingChannel = ECG_VIEWER_CONFIG_STORE.zoomingChannel;
    if (this.isInZoomMode) {
      this.lastPrevChanIdx = ECG_VIEWER_CONFIG_STORE.prevChanIdx;
    }
    this.beforeZoomValue = ECG_VIEWER_CONFIG_STORE.beforeZoomValue;

    this.isInViewAllMode = ECG_VIEWER_CONFIG_STORE.isInViewAllMode;
    this.beforeViewAllValue = ECG_VIEWER_CONFIG_STORE.beforeViewAllValue;

    this.canvasBackground = new fabric.StaticCanvas(idComponents.canvasBackground, {
      renderOnAddRemove: false,
      width: this.config.width,
      height: this.config.height,
    });

    this.exportGridCanvas = new fabric.StaticCanvas(idComponents.exportGridCanvas, {
      renderOnAddRemove: false,
      width: this.config.width,
      height: this.config.height,
      backgroundColor,
    });

    this.canvasChart = new fabric.Canvas(idComponents.canvasChart, {
      renderOnAddRemove: false,
      width: this.config.width,
      height: this.config.height,
      selection: false,
    });

    this.canvasObject = new fabric.Canvas(idComponents.canvasObject, {
      renderOnAddRemove: false,
      width: this.config.width,
      height: this.config.height,
      selection: false,
      skipTargetFind: false,
      fireRightClick: true,
    });

    if (this.isDisplayScrollbar) {
      this.canvasScroller = new fabric.Canvas(idComponents.canvasScroller, {
        renderOnAddRemove: false,
        width: this.config.width,
        height: this.config.scrollerHeight,
        selection: false,
      });
    }

    this.loadFullECG(this.offsetX);
    this.annotation = new Annotations(
      this.canvasObject,
      this.config,
      this.verticalChannelPlacement,
    );
    // *: Import objects from JSON
    if (annotations && !_.isEmpty(annotations)) {
      this.importJSONObject(annotations, true);
    }

    if (!_.isEmpty(events)) {
      this.events = events;
      this.drawEventsPosition(events, true);
    }

    const { jumpOffset, beatPos, sampleLength, sampleRange } = this.config;

    if (!_.isNil(beatPos)) {
      this.drawBeatPosition(beatPos, true);
    }

    if (jumpOffset) {
      this.offsetX = jumpOffset;
      if (this.offsetX >= sampleLength - sampleRange) {
        this.offsetX = sampleLength - sampleRange;
      }
      if (this.offsetX < 0) {
        this.offsetX = 0;
      }
      this.annotation.scroll(this.offsetX, this.displayedChannelsArr);
      this.loadECG(this.offsetX);
    }
    this.isDownLeft = false;
    this.isDownRight = false;
    this.actionType = null;
    this.isExporting = false;

    // *: Inconsolata is loaded from a CDN and may arrive after the first paint; fabric would keep
    // *: the fallback-font measurement and leave beat-marker labels mis-centered. Re-measure once
    // *: the real font is available.
    this.reMeasureBeatMarkersWhenFontReady();

    this.canvasObject.on('mouse:down', (event: any) => {
      this.cvObjMouseDown(event);
    });

    this.canvasObject.on('mouse:move', (event: any) => {
      this.cvObjMouseMove(event);
    });

    this.canvasObject.on('mouse:up', (event: any) => {
      this.cvObjMouseUp(event);
    });

    if (this.isDisplayScrollbar) {
      window.addEventListener('keydown', this.eventNavigateFrame, true);
      this.canvasObject.on('mouse:wheel', (event: any) => {
        this.cvObjMouseWheel(event);
      });

      this.canvasScroller.on('mouse:down', (event: any) => {
        this.cvScMouseDown(event);
      });

      this.canvasScroller.on('mouse:move', (event: any) => {
        this.cvScMouseMove(event);
      });

      this.canvasScroller.on('mouse:up', (event: any) => {
        this.cvScMouseUp(event);
      });

      this.canvasScroller.on('mouse:out', () => {
        this.cvScMouseOut();
      });
    }

    this.requestAnimationFrameId = null;
    this.mouseEventInfo = null;
  };

  AIECGChart.prototype.handleCvObjMouseEvent = function () {
    if (this.mouseEventInfo) {
      const { type, movementX } = this.mouseEventInfo;
      switch (type) {
        case MOUSE_EVENT_TYPE.MOUSE_WHEEL: {
          this.handleScrollOnMove(movementX);
          break;
        }
        case MOUSE_EVENT_TYPE.MOUSE_MOVE: {
          if (this.isPickingDate && this.annotation) {
            this.annotation.updatePickDateIndicator({ point: this.pointOnMove });
          }
          if (
            [ANNOTATION_TYPE.HLINE, ANNOTATION_TYPE.VLINE].includes(this.actionType) &&
            this.annotation
          ) {
            this.annotation.update(
              this.pointOnMove,
              true,
              this.displayedChannelsArr,
              null,
              null,
              this.markers.beatMarkers,
              this.offsetX,
            );
          } else if (
            this.actionType === ANNOTATION_TYPE.RECT_SELECT &&
            !this.isScrollingChart &&
            this.annotation
          ) {
            this.annotation.update(
              this.pointOnMove,
              true,
              this.displayedChannelsArr,
              null,
              null,
              this.markers.beatMarkers,
              this.offsetX,
            );
          } else if (this.annotation?.isModifying) {
            this.annotation.update(
              this.pointOnMove,
              false,
              this.displayedChannelsArr,
              null,
              null,
              this.markers.beatMarkers,
              this.offsetX,
            );
            if (this.zoomingChannel === null || typeof this.zoomingChannel === 'undefined') {
              this.annotation.updateChannelIndex(this.displayedChannelsArr);
            }
          } else if (
            this.actionType !== ACTION_TYPE.AR_BEAT_MARKER &&
            !this.isRectSelectDone &&
            !this.isHLineDone &&
            !this.isVLineDone &&
            this.isDownLeft &&
            !this.isPickingDate
          ) {
            // * Prevent to scroll with right mouse click
            this.isScrollingChart = true;
            this.changeCursor(MOUSE_CURSOR.MOVE);
            this.handleScrollOnMove(movementX);
          }
          if (this.actionType === ACTION_TYPE.AR_BEAT_MARKER && this.annotation) {
            this.annotation.updateBeatIndicator({ point: this.pointOnMove });
            if (this.isDrawingBeatSelect) {
              this.annotation.updateBeatIndicator({ visible: false });
              this.propsFunction.updateSelectingBeatStatus(this.isDrawingBeatSelect);
              if (!this.isScrollingChart) {
                const pointOnMoveSample =
                  Math.round(this.pointOnMove.x / this.config.indexToPix) + this.offsetX;
                const startSample = Math.min(this.contextMenuSample, pointOnMoveSample);
                const endSample = Math.max(this.contextMenuSample, pointOnMoveSample);
                this.markers.handleSelectBeat({
                  startSample,
                  endSample,
                  beatTypeNumber: this.beatTypeNumber,
                });
                this.annotation.updateBeatSelection(
                  this.pointOnMove,
                  null,
                  null,
                  isDeleteBeatMode(this.beatTypeNumber),
                );
              }
            } else {
              this.markers.handleHoverBeat(
                this.pointOnMove,
                this.offsetX,
                isDeleteBeatMode(this.beatTypeNumber),
              );
            }
          }
          break;
        }
        default: {
          break;
        }
      }
      this.requestAnimationFrameId = null;
    }
  };

  AIECGChart.prototype.handleCvScMouseEvent = function () {
    if (this.mouseEventInfo) {
      const { type, xPoint } = this.mouseEventInfo;
      switch (type) {
        case MOUSE_EVENT_TYPE.MOUSE_WHEEL: {
          break;
        }
        case MOUSE_EVENT_TYPE.MOUSE_MOVE: {
          this.isScrollingSelectionRange = true;
          const { maxIndex, minIndex, width, sampleLength, sampleRange } = this.config;
          this.offsetX = Math.round((xPoint * sampleLength) / width - sampleRange / 2);
          this.offsetX = this.offsetX < maxIndex ? this.offsetX : maxIndex;
          this.offsetX = this.offsetX > minIndex ? this.offsetX : minIndex;
          this.annotation.scroll(this.offsetX, this.displayedChannelsArr);
          this.loadECG(this.offsetX);
          break;
        }
        default: {
          break;
        }
      }
      this.requestAnimationFrameId = null;
    }
  };

  AIECGChart.prototype.cvObjMouseDown = function (event: any) {
    // *: De-select object after changing to View All mode
    if (this.isInViewAllMode) {
      this.actionType = null;
      this.annotation.selectedObj = null;
      this.ecgView.specifySampleMark = null;
      this.ecgView.specifyPattern = null;
    }
    const point = this.canvasObject.getPointer(event.e);
    const clickedChannelIndex = getClosestChannelIndex(point.y, this.verticalChannelPlacement);
    const { button } = event;
    this.isDownRight = button === 3;
    this.isDownLeft = button === 1;
    // *: Calculate sample mark offsetX
    this.clickedOffsetX = Math.round(point.x / this.config.indexToPix) + this.offsetX;
    if (this.actionType) {
      if (this.isDownLeft) {
        switch (this.actionType) {
          case ANNOTATION_TYPE.HLINE: {
            this.annotation.update(
              point,
              true,
              this.displayedChannelsArr,
              null,
              null,
              this.markers.beatMarkers,
              this.offsetX,
            );
            if (this.zoomingChannel === null || typeof this.zoomingChannel === 'undefined') {
              this.annotation.updateChannelIndex(this.displayedChannelsArr);
            }
            this.actionType = null;
            this.isHLineDone = true;
            this.lastPointOnMove = null;
            this.scrollDirection = null;
            this.pointOnMove = null;
            this.haveEntered = false;
            this.isScrollingChart = false;
            this.propsFunction.updateSelectingHLineStatus(true);
            this.propsFunction.updateCompletedStatus(false, false);
            window.removeEventListener('keyup', this.eventCancelDrawHLine);
            break;
          }
          case ANNOTATION_TYPE.VLINE: {
            this.annotation.update(
              point,
              true,
              this.displayedChannelsArr,
              null,
              null,
              this.markers.beatMarkers,
              this.offsetX,
            );
            if (this.zoomingChannel === null || typeof this.zoomingChannel === 'undefined') {
              this.annotation.updateChannelIndex(this.displayedChannelsArr);
            }
            this.actionType = null;
            this.isVLineDone = true;
            this.lastPointOnMove = null;
            this.scrollDirection = null;
            this.pointOnMove = null;
            this.haveEntered = false;
            this.isScrollingChart = false;
            this.propsFunction.updateSelectingVLineStatus(true);
            this.propsFunction.updateCompletedStatus(false, false);
            window.removeEventListener('keyup', this.eventCancelDrawVLine, false);
            break;
          }
          case ANNOTATION_TYPE.RECT_SELECT: {
            const startSample = Math.min(this.contextMenuSample, this.clickedOffsetX);
            const endSample = Math.max(this.contextMenuSample, this.clickedOffsetX);
            const specifyPattern = {
              startSample,
              endSample,
              channelIndex: this.displayedChannelsArr[this.annotation.selectedObj.channelIndex],
            };
            this.ecgView.drawPattern(this.offsetX, specifyPattern);
            this.annotation.deleteObj(false);
            this.actionType = null;
            this.isRectSelectDone = true;
            this.lastPointOnMove = null;
            this.scrollDirection = null;
            this.pointOnMove = null;
            this.haveEntered = false;
            this.isScrollingChart = false;
            this.isDrawingRectSelect = false;
            this.propsFunction.updateCompletedStatus(false, false);
            this.propsFunction.updateSelectingPatternStatus(true);
            this.renderECG();
            window.removeEventListener('keyup', this.eventCancelDrawRectSelect);
            break;
          }
          case ACTION_TYPE.AR_BEAT_MARKER: {
            this.saveContextMenuPosition(point);
            this.isDrawingBeatSelect = true;
            this.annotation.drawBeatSelection(
              this.contextMenuPoint,
              this.offsetX,
              isDeleteBeatMode(this.beatTypeNumber),
            );
            break;
          }
          case ACTION_TYPE.ZOOMING: {
            this.zoomEnd = true;
            this.actionType = null;
            this.propsFunction.updateCompletedStatus(false, true);
            this.zoom(clickedChannelIndex, point.x);
            window.removeEventListener('keyup', this.eventCancelZoom);
            break;
          }
          default: {
            break;
          }
        }
      }
    } else if (this.isDownLeft) {
      // *: Prevent select objects when is in View All mode
      if (!this.isInViewAllMode) {
        this.annotation.selectObj(point);
        this.renderECG();
        // *: Click to complete/cancel draw text
        if (!this.annotation.selectedObj && this.isDrawingText) {
          this.isDrawingText = false;
          window.removeEventListener('keyup', this.eventCancelDrawText);
          this.propsFunction.updateCompletedStatus(false, false);
        }
        this.ecgView.selectMark(
          this.clickedOffsetX,
          { x: point.x, y: point.y },
          clickedChannelIndex,
        );
        this.ecgView.selectPattern(
          this.clickedOffsetX,
          { x: point.x, y: point.y },
          clickedChannelIndex,
        );
      }
      // *: Check click on object/sample mark/pattern when is in view all mode
      if (
        this.isInViewAllMode &&
        !this.annotation.selectedObj &&
        !this.ecgView.specifySampleMark &&
        !this.ecgView.specifyPattern &&
        this.clickedOffsetX <= this.config.sampleLength
      ) {
        this.isClickedViewAll = true;
        this.propsFunction.toggleViewAllMode();
        this.exitViewAllMode();
      }
      this.propsFunction.updateSelectingHLineStatus(
        this.annotation.selectedObj && this.annotation.selectedObj.type === ANNOTATION_TYPE.HLINE,
      );
      this.propsFunction.updateSelectingVLineStatus(
        this.annotation.selectedObj && this.annotation.selectedObj.type === ANNOTATION_TYPE.VLINE,
      );
      this.propsFunction.updateSelectingPatternStatus(this.ecgView.specifyPattern);
    } else if (this.isDownRight) {
      if (!this.isDrawingText) {
        this.propsFunction.updateCompletedStatus(false, false);
        // *: Get selected beat marker status to determine which rendered in Context menu
        const selectedBeatMarkerObject = this.markers.checkBeatAtPosition(point, this.offsetX);
        if (selectedBeatMarkerObject) {
          this.propsFunction.updateSelectedBeatMarker(
            selectedBeatMarkerObject,
            this.clickedOffsetX,
          );
        } else {
          this.propsFunction.updateSelectedBeatMarker(null, this.clickedOffsetX);
        }
      }
    }
  };

  AIECGChart.prototype.cvObjMouseWheel = function (event: any) {
    if (
      this.isDrawingHLine ||
      this.isDrawingRectSelect ||
      this.isDrawingBeatSelect ||
      this.isDrawingVLine ||
      this.isDrawingText ||
      this.isInViewAllMode
    ) {
      return;
    }
    event.e.preventDefault();
    this.pointOnMove = this.canvasObject.getPointer(event.e);
    const { deltaY, deltaX } = event.e;
    //* Support scroll horizontal and vertical by mouse and trackpad
    if (this.requestAnimationFrameId) {
      window.cancelAnimationFrame(this.requestAnimationFrameId);
    }
    this.mouseEventInfo = {
      type: MOUSE_EVENT_TYPE.MOUSE_WHEEL,
      movementX: -(deltaX || deltaY) * 2,
    };
    this.requestAnimationFrameId = window.requestAnimationFrame(() => this.handleCvObjMouseEvent());
  };

  AIECGChart.prototype.cvObjMouseMove = function (event: any) {
    const isDown = this.isDownLeft || this.isDownRight;
    if (
      (!isDown &&
        !this.isDrawingHLine &&
        !this.isDrawingRectSelect &&
        !this.isDrawingBeatSelect &&
        !this.isDrawingVLine &&
        this.actionType !== ACTION_TYPE.AR_BEAT_MARKER &&
        !this.isPickingDate) ||
      this.isInViewAllMode
    ) {
      return;
    }
    if (this.haveEntered) {
      if (this.scrollDirection) {
        this.pointOnMove = this.lastPointOnMove
          ? this.lastPointOnMove
          : this.canvasObject.getPointer(event.e);
        this.pointOnMove.x = this.scrollDirection === 'left' ? 0 : this.config.width;
      } else {
        this.pointOnMove = this.lastPointOnMove
          ? this.lastPointOnMove
          : this.canvasObject.getPointer(event.e);
      }
      this.haveEntered = false;
    } else {
      this.pointOnMove = this.canvasObject.getPointer(event.e);
    }
    if (this.requestAnimationFrameId) {
      window.cancelAnimationFrame(this.requestAnimationFrameId);
    }
    this.mouseEventInfo = {
      type: MOUSE_EVENT_TYPE.MOUSE_MOVE,
      movementX: event.e.movementX,
    };
    this.requestAnimationFrameId = window.requestAnimationFrame(() => this.handleCvObjMouseEvent());
  };

  AIECGChart.prototype.cvObjMouseUp = function (event: any) {
    if (this.isInViewAllMode) {
      return;
    }
    this.isDownLeft = false;
    this.isDownRight = false;
    const { button } = event;
    if (this.isPickingDate && button === 1) {
      this.pickDate();
    }
    if (this.actionType === ACTION_TYPE.AR_BEAT_MARKER) {
      const point = this.canvasObject.getPointer(event.e);
      // Drag click detect
      const mouseUpOffsetX = Math.round(point.x / this.config.indexToPix) + this.offsetX;
      const startSample = Math.min(this.contextMenuSample, mouseUpOffsetX);
      const endSample = Math.max(this.contextMenuSample, mouseUpOffsetX);
      const isClickOnly = endSample - startSample === 0;
      //
      const selectedBeatMarkerObject = this.markers.checkBeatAtPosition(point, this.offsetX);
      const isClickInEmptySpace = _.isEmpty(selectedBeatMarkerObject);
      if (button === 1) {
        if (isDeleteBeatMode(this.beatTypeNumber)) {
          if (isClickOnly) {
            if (!isClickInEmptySpace) {
              const { selectedBeatMarker, selectedBeatMarkerIndex } = selectedBeatMarkerObject;
              this.deleteBeatMarker(selectedBeatMarker, selectedBeatMarkerIndex);
            }
          } else {
            this.markers.deleteMultipleBeat({ startSample, endSample, offsetX: this.offsetX });
            this.handleUpdateBelowText();
            this.handleCallbackUpdateBeat();
            this.markers.deselectBeat();
          }
        } else if (isClickOnly) {
          const rangesWithin = this.calculateRangeData(
            TYPE_MODE.ADD_BEAT,
            0,
            this.ecgDataMap,
            this.timeObject.startMoment,
            this.timeObject.timezoneOffset,
          );
          if (rangesWithin) {
            if (isClickInEmptySpace) {
              this.markers.addBeat(this.clickedOffsetX, this.offsetX, this.beatTypeNumber);
              this.handleUpdateBelowText();
              this.handleCallbackUpdateBeat();
            } else {
              const { selectedBeatMarker, selectedBeatMarkerIndex, selectedBeatTypeNumber } =
                selectedBeatMarkerObject;
              if (!isSameBeats(selectedBeatTypeNumber, this.beatTypeNumber)) {
                this.changeBeatMarkerType(
                  selectedBeatMarker,
                  selectedBeatMarkerIndex,
                  this.beatTypeNumber,
                );
                this.markers.resetHoverBeat();
              }
            }
          } else {
            this.propsFunction.handleOpenSnackbar('Beat Marker cannot be added');
            toastrError('Beat Marker cannot be added to a range without data', 'Error');
          }
        } else {
          this.markers.changeMultipleBeatType(
            startSample,
            endSample,
            this.offsetX,
            this.beatTypeNumber,
          );
          this.handleCallbackUpdateBeat();
          this.markers.deselectBeat();
        }
        this.annotation.updateBeatIndicator({ visible: true });
        this.annotation.deleteBeatSelection();
        this.isDrawingBeatSelect = false;
        this.propsFunction.updateSelectingBeatStatus(this.isDrawingBeatSelect);
        this.scrollDirection = null;
        this.pointOnMove = null;
        this.haveEntered = false;
        this.isScrollingChart = false;
        this.renderECG();
        clearInterval(this.scrollInterval);
      }
      if (button === 3) {
        if (!isDeleteBeatMode(this.beatTypeNumber) && !isClickInEmptySpace) {
          const { selectedBeatMarker, selectedBeatMarkerIndex } = selectedBeatMarkerObject;
          this.deleteBeatMarker(selectedBeatMarker, selectedBeatMarkerIndex);
        }
        this.annotation.updateBeatIndicator({ visible: true });
        this.annotation.deleteBeatSelection();
        this.isDrawingBeatSelect = false;
        this.propsFunction.updateSelectingBeatStatus(this.isDrawingBeatSelect);
        this.scrollDirection = null;
        this.pointOnMove = null;
        this.haveEntered = false;
        this.isScrollingChart = false;
        this.renderECG();
        clearInterval(this.scrollInterval);
      }
      return;
    }

    if (!this.isDrawingHLine && !this.isDrawingRectSelect && !this.isDrawingVLine) {
      if (!this.isScrollingChart && !this.zoomEnd && button === 1) {
        // *: Only select with left click
        // *: Selected sample mark/pattern
        this.ecgView.drawSelectedMark(this.offsetX);
        this.ecgView.drawSelectedPattern(this.offsetX);
      }
      this.annotation.isModifying = false;
      this.isScrollingChart = false;
      this.zoomEnd = false;
      // *: Clear data
      this.annotation.prevPoint = null;
      // *: Handle for left click while is in Mode (do not reset to default cursor)
      if (!this.actionType) {
        this.changeCursor(MOUSE_CURSOR.DEFAULT);
      }
    }

    // *: Prevent scroll when is drawing horizontal line/pattern
    if (this.isRectSelectDone) {
      this.isRectSelectDone = false;
      this.isDrawingRectSelect = false;
    }

    if (this.isHLineDone) {
      this.changeCursor(MOUSE_CURSOR.DEFAULT);
      this.isHLineDone = false;
      this.isDrawingHLine = false;
    }

    if (this.isVLineDone) {
      this.changeCursor(MOUSE_CURSOR.DEFAULT);
      this.isVLineDone = false;
      this.isDrawingVLine = false;
    }
  };

  AIECGChart.prototype.cvScMouseDown = function (event: any) {
    const { x, y } = event.pointer;
    this.isMouseDownScroller = this.scroller.checkClickInsideSelectionRange(x, y);
  };

  AIECGChart.prototype.cvScMouseMove = function (event: any) {
    const { x, y } = event.pointer;
    if (!this.isMouseDownScroller) {
      this.isMouseOverScroller = this.scroller.checkClickInsideSelectionRange(x, y);
      this.isMouseUpScroller = this.isMouseOverScroller;
      if (this.isMouseOverScroller && !this.isDrawScrollerTrue) {
        this.scroller.draw(this.offsetX, true);
        this.isDrawScrollerTrue = true;
        this.isDrawScrollerFalse = false;
      }
      if (!this.isMouseOverScroller && !this.isDrawScrollerFalse) {
        this.scroller.draw(this.offsetX, false);
        this.isDrawScrollerFalse = true;
        this.isDrawScrollerTrue = false;
      }
      return;
    }
    if (this.requestAnimationFrameId) {
      window.cancelAnimationFrame(this.requestAnimationFrameId);
    }
    this.mouseEventInfo = {
      type: MOUSE_EVENT_TYPE.MOUSE_MOVE,
      movementX: event.e.movementX,
      xPoint: event.pointer.x,
    };
    this.requestAnimationFrameId = window.requestAnimationFrame(() => this.handleCvScMouseEvent());
  };

  AIECGChart.prototype.cvScMouseUp = function (event: any) {
    const { x, y } = event.pointer;
    const { sampleLength, sampleRange, maxIndex, minIndex, width } = this.config;

    this.isMouseDownScroller = false;

    if (!this.isScrollingSelectionRange) {
      // *: Do not re-draw scroller after scroll selection range
      this.isMouseUpScroller = true;

      this.offsetX = Math.round((x * sampleLength) / width - sampleRange / 2);
      this.offsetX = this.offsetX < maxIndex ? this.offsetX : maxIndex;
      this.offsetX = this.offsetX > minIndex ? this.offsetX : minIndex;

      this.annotation.scroll(this.offsetX, this.displayedChannelsArr);
      this.loadECG(this.offsetX);
    } else {
      this.isScrollingSelectionRange = false;
      this.isMouseUpScroller = this.scroller.checkClickInsideSelectionRange(x, y);
      this.isMouseOverScroller = this.isMouseUpScroller;
      this.isDrawScrollerFalse = false;
      this.isDrawScrollerTrue = false;
      this.loadECG(this.offsetX);
    }
  };

  AIECGChart.prototype.cvScMouseOut = function () {
    if (!this.isScrollingSelectionRange) {
      this.isMouseUpScroller = false;
      this.isMouseOverScroller = false;
      this.isMouseDownScroller = false;
      this.isDrawScrollerFalse = false;
      this.isDrawScrollerTrue = false;
      this.scroller.draw(this.offsetX, false);
    }
  };

  AIECGChart.prototype.importJSONObject = function (objectArray: any) {
    objectArray.forEach((object: any) => {
      this.annotation.scroll(this.offsetX, this.displayedChannelsArr);

      if (
        object.type === ANNOTATION_TYPE.SAMPLE_MARK ||
        object.type === ANNOTATION_TYPE.PATTERN ||
        object.type === ANNOTATION_TYPE.AFIB_EVENT
      ) {
        this.ecgView.importMark(this.offsetX, object);
      } else {
        this.annotation.import(object);
      }
    });
    this.updateConfig();
  };

  AIECGChart.prototype.reMeasureBeatMarkersWhenFontReady = function () {
    if (typeof document === 'undefined' || !document.fonts) {
      return;
    }
    const font = '10px "Inconsolata"'; // *: on-screen beat-marker font (DEFAULT_FONT)
    if (document.fonts.check(font)) {
      return; // *: already loaded — the initial measurement is correct
    }
    // *: catch().then() runs whether the CDN font loads or fails, so we never hang on the
    // *: fallback measurement. clearFabricFontCache() must be arg-less: fabric keys the cache by
    // *: the full fontFamily string ('inconsolata, arial, ...'), not by 'Inconsolata' alone.
    document.fonts
      .load(font)
      .catch(() => {})
      .then(() => {
        if (fabric.util) {
          fabric.util.clearFabricFontCache();
        }
        if (this.markers) {
          this.markers.reDrawBeatMarkers(this.offsetX, this.isInViewAllMode);
          this.renderECG();
        }
      });
  };

  AIECGChart.prototype.loadECG = function (offsetX: any) {
    if (!this.ecgView) {
      this.ecgView = new EcgView(
        this.canvasChart,
        this.pixelSamples,
        this.config,
        this.displayedChannelsArr,
        this.verticalChannelPlacement,
      );
    }
    this.ecgView.draw(offsetX);

    if (this.isDisplayScrollbar) {
      if (!this.scroller) {
        this.scroller = new Scroller(
          this.canvasScroller,
          this.samplesScroller,
          this.config,
          this.config.prevChanIdx,
        );
      }
      this.scroller.draw(
        offsetX,
        this.isMouseOverScroller || this.isMouseDownScroller || this.isMouseUpScroller,
      );
    }

    if (!this.markers) {
      this.markers = new BeatMarkers(
        this.canvasObject,
        this.currentBeatMarkers,
        this.verticalChannelPlacement,
        this.config,
        this.isAppliedAI,
        this.isShowVES,
        this.isShowSVES,
        this.currentHesBeatStatus,
        this.aiData,
        this.propsFunction.handleOpenSnackbar,
      );
    }

    this.markers.draw(offsetX, this.isInViewAllMode);

    this.bringEventIndicatorToFront();

    if (!this.titleChart) {
      this.titleChart = new TitleChart(
        this.canvasObject,
        this.verticalChannelPlacement,
        this.config,
        this.displayedChannelsArr,
        this.timeObject,
      );
    }
    this.titleChart.draw(offsetX);
    this.renderECG();
  };

  AIECGChart.prototype.loadFullECG = function (offsetX: any) {
    if (!this.gridBackGround) {
      const configGrid = {
        width: this.config.width,
        height: this.config.height,
        majorDivSize: this.config.majorDivSize,
      };
      this.gridBackGround = new Grid(this.canvasBackground, configGrid);
    }

    if (!this.exportGrid) {
      const configGrid = {
        width: this.config.width,
        height: this.config.height,
        majorDivSize: this.config.exportMajorDivSize,
      };
      this.exportGrid = new Grid(this.exportGridCanvas, configGrid);
    }
    // this.exportGrid.drawExportGrid();

    // *: Do not draw grid when in View All Mode
    if (!this.isInViewAllMode) {
      this.gridBackGround.draw();
    } else {
      this.gridBackGround.drawBorder();
    }
    this.loadECG(offsetX);
  };

  AIECGChart.prototype.renderECG = function () {
    this.canvasObject.renderAll();
  };

  AIECGChart.prototype.cleanupData = function () {
    window.removeEventListener('keydown', this.eventNavigateFrame, true);
    this.removeAllEventListener();
    this.B0 = null;
    this.aiData = null;
    this.gridBackGround = null;
    this.markers = null;
    this.titleChart = null;
    this.propsFunction = null;

    this.currentBeatMarkers = null;
    this.currentHesBeatStatus = null;
    this.samples = null;
    this.pixelSamples = null;
    this.rawDataFromNetwork = null;
    this.rawData = null;
    this.rawDataZoom = null;
    this.originalSamples = null;

    this.canvasBackground.dispose();
    this.canvasBackground = null;
    this.canvasChart.dispose();
    this.canvasChart = null;
    this.canvasObject.dispose();
    this.canvasObject = null;
    if (this.isDisplayScrollbar) {
      this.samplesScroller = null;
      this.canvasScroller.dispose();
      this.canvasScroller = null;
      this.scroller = null;
    }
    this.exportGridCanvas.dispose();
    this.exportGridCanvas = null;
    this.exportGrid = null;
    this.ecgView = null;
    this.annotation = null;
    console.log('Cleaned up data');
  };

  AIECGChart.prototype.getCurrentTime = function () {
    return this.titleChart.getTime(this.offsetX);
  };

  AIECGChart.prototype.jumpTo = function (jumpPos: any, portion: any = 0) {
    const { sampleLength, sampleRange, width, samplingRate, wndDuration } = this.config;
    if (portion === 0) {
      this.offsetX = jumpPos;
    } else {
      const windowPos = Math.round(((width / portion) * samplingRate * wndDuration) / width);
      this.offsetX = jumpPos - windowPos;
    }
    if (this.offsetX >= sampleLength - sampleRange) {
      this.offsetX = sampleLength - sampleRange;
    }
    if (this.offsetX < 0) {
      this.offsetX = 0;
    }
    this.annotation.scroll(this.offsetX, this.displayedChannelsArr);
    this.loadECG(this.offsetX);
  };

  AIECGChart.prototype.initValue = function (
    rawDataFromNetwork: any,
    config: any,
    beatMarkerArr: any,
    hesBeatStatus: any,
    hiddenChannels: any,
    missingSampleLength: any,
  ) {
    if (rawDataFromNetwork && rawDataFromNetwork.length) {
      this.displayedChannelsArr = [];
      hiddenChannels.forEach((hiddenChannel: any) => {
        this.displayedChannelsArr.push(hiddenChannel);
      });

      this.rawDataFromNetwork = [...rawDataFromNetwork];
      this.rawData = this.calculateRawDataFromNetwork(rawDataFromNetwork);
      this.samples = [];
      this.B0 = [];
      this.pixelSamples = [];
      this.verticalChannelPlacement = [];

      this.config = {
        samplingRate: config.samplingRate || 250,
        numberOfVisibleChannels: rawDataFromNetwork.length,
        defaultSpeed: 25.0,
        gain: 10.0,
        adcZero: 0,
        adcGain: 655.35875,
        someChannelsHidden: this.displayedChannelsArr.length !== 3,
        researchMode: false,
        LFF: 0,
        HFF: 0,
        hffOrder: 8,
        bccHFF: 15,
        bccMinSR: 200,
        width: config.width ?? 945,
        height: config.height ?? 250,
        defaultWidth: 945,
        defaultHeight: 250,
        lineHeight: 250 / 3,
        defaultWndDuration: 10,
        aspectRatioX: 3.78,
        aspectRatioY: 3.78,
        minIndex: 0,
        sampleLength: rawDataFromNetwork[0].length,
        prevChanIdx: 1,
        exportWndDuration: 10,
        exportSpeed: 25,
        exportGain: 10,
        exportFineGrid: false,
        exportNumberOfVisibleChannels: rawDataFromNetwork.length,
      };

      this.config.recDuration =
        this.rawData.length / this.config.samplingRate / this.config.numberOfVisibleChannels;
      this.viewAllProportion = this.config.recDuration / 10;
      this.config.speed = this.config.defaultSpeed;
      this.config.wndDuration = this.config.defaultWndDuration;

      if (this.isDisplayScrollbar) {
        this.config.scrollerHeight = 31;
        this.config.defaultScrollerHeight = 31;
      }

      this.exportDisplayedChannelsArr = this.displayedChannelsArr;

      this.isInViewAllMode = false;

      if (config) {
        this.config = _.assign(this.config, config);
      }

      this.originalBeatMarkers = beatMarkerArr;
      this.currentBeatMarkers = beatMarkerArr;
      this.originalHesBeatStatus = hesBeatStatus;
      this.currentHesBeatStatus = hesBeatStatus;

      this.calculateConfig();
      this.calculateSamplesFromRawData(this.rawData, missingSampleLength);
      this.propsFunction.updateSpdMenu(this.config.speed);
    } else {
      this.config = {
        samplingRate: config.samplingRate || 250,
        numberOfVisibleChannels: 0,
        defaultSpeed: 25.0,
        gain: 10.0,
        adcZero: 0,
        adcGain: 655.35875,
        someChannelsHidden: false,
        researchMode: false,
        LFF: 0,
        HFF: 0,
        hffOrder: 8,
        bccHFF: 15,
        bccMinSR: 200,
        width: config.width ?? 945,
        height: config.height ?? 250,
        defaultWidth: 945,
        defaultHeight: 250,
        lineHeight: 250 / 3,
        defaultWndDuration: 10,
        aspectRatioX: 3.78,
        aspectRatioY: 3.78,
        minIndex: 0,
        sampleLength: 0,
        prevChanIdx: 1,
        exportWndDuration: 10,
        exportSpeed: 25,
        exportGain: 10,
        exportNumberOfVisibleChannels: 0,
        exportFineGrid: false,
      };

      this.config.recDuration = 10;
      this.viewAllProportion = this.config.recDuration / 10;
      this.config.speed = this.config.defaultSpeed;
      this.config.wndDuration = this.config.defaultWndDuration;

      if (this.isDisplayScrollbar) {
        this.config.scrollerHeight = 31;
        this.config.defaultScrollerHeight = 31;
      }

      this.exportDisplayedChannelsArr = this.displayedChannelsArr;

      const objectConfigUpdate = reCalculateConfig(this.config);
      const channelLabels: any[] = [];
      this.config = { ...this.config, ...objectConfigUpdate, channelLabels };
    }
  };

  AIECGChart.prototype.calculateConfig = function () {
    const channelLabels = new Array(this.displayedChannelsArr.length);
    this.displayedChannelsArr.forEach((channel: any, index: any) => {
      channelLabels[index] = `${textChannel[channel]}`;
    });
    const objectConfigUpdate = reCalculateConfig(this.config);
    this.config = { ...this.config, ...objectConfigUpdate, channelLabels };
  };

  AIECGChart.prototype.calculateRawDataFromNetwork = function (rawDataFromNetwork: any) {
    if (rawDataFromNetwork && rawDataFromNetwork.length) {
      const rawData = [];
      const firstRawDataFromNetworkLength = rawDataFromNetwork[0].length;
      const rawDataFromNetworkLength = rawDataFromNetwork.length;
      for (let i = 0; i < firstRawDataFromNetworkLength; i += 1) {
        for (let j = 0; j < rawDataFromNetworkLength; j += 1) {
          rawData.push(rawDataFromNetwork[j][i]);
        }
      }
      return rawData;
    }
    return [[], [], []];
  };

  AIECGChart.prototype.calculatePixelSamplesFromSamples = function (samples: any) {
    const { height, numberOfVisibleChannels, millivoltToPix, indexToPix } = this.config;
    const samplesLength = samples.length;
    this.verticalChannelPlacement = samples.map((chArr: any, chIdx: any) => {
      if (chIdx !== samplesLength - 1) {
        return (height / numberOfVisibleChannels) * (chIdx + 0.5) + 18.9; // *: Translate 1 default square
      }

      return (height / numberOfVisibleChannels) * (chIdx + 0.5);
    });

    this.pixelSamples = samples.map((chArr: any, chIdx: any) =>
      chArr.map((u: any, i: any) => ({
        x: Math.floor(i * indexToPix),
        y: Math.floor(this.verticalChannelPlacement[chIdx] - u * millivoltToPix),
      })),
    );
  };

  AIECGChart.prototype.calculateSamplesFromRawData = function (
    rawData: any,
    missingSampleLength: any = 0,
  ) {
    const { samplingRate, numberOfVisibleChannels, adcZero, adcGain, someChannelsHidden } =
      this.config;

    const samples = rawData.map((n: any) => (n - adcZero) / adcGain);
    const samplesPerChannel = rawData.length / numberOfVisibleChannels;
    this.samples = new Array(numberOfVisibleChannels);
    const samplesLength = this.samples.length;
    for (let j = 0; j < samplesLength; j += 1) {
      this.samples[j] = new Array(samplesPerChannel);
    }

    samples.reduce((allChannels: any, newSample: any, index: any) => {
      allChannels[index % numberOfVisibleChannels][Math.floor(index / numberOfVisibleChannels)] =
        newSample;
      return allChannels;
    }, this.samples);

    this.B0 = this.samples.map(median);
    this.samples.forEach((chArr: any, chIdx: any) => {
      const chArrLength = chArr.length;
      for (let j = 0; j < chArrLength; j += 1) {
        chArr[j] -= this.B0[chIdx];
      }
    });

    this.originalSamples = this.samples.map((chArr: any) => chArr.slice(0));

    if (this.isDisplayScrollbar) {
      this.samplesScroller = this.samples.map((chArr: any) => chArr.slice(0));
    }

    if (someChannelsHidden) {
      this.samples = this.displayedChannelsArr.map((chIdx: any) =>
        this.originalSamples[chIdx].slice(0),
      );
      this.config.numberOfVisibleChannels = this.samples.length;
    }

    this.filterSamples(false);
    this.calculatePixelSamplesFromSamples(this.samples);

    // const firstChannelADC = rawData.filter((pt: any, index: any) => (index % numberOfVisibleChannels === 0));
    const diagnosisLeadChannelADC = [];
    const rawDataLength = rawData.length;
    for (let i = this.calculateBeatMarkerChannel - 1; i < rawDataLength; i += 3) {
      diagnosisLeadChannelADC.push(rawData[i]);
    }

    // * Remove sinewave was added to missing data and fill it to null before calculating beat markers
    const clonedDiagnosisLeadChannelADC = [...diagnosisLeadChannelADC];
    const removedSineWaveDataArray = clonedDiagnosisLeadChannelADC.splice(missingSampleLength);
    const filledNullMissingSampleDataArray = new Array(missingSampleLength).fill(null);
    const expectedDiagnosisLeadChannelADC = [
      ...filledNullMissingSampleDataArray,
      ...removedSineWaveDataArray,
    ];

    // * Always calculate beat markers by using Pantompkin
    this.pantompkinBeatMarkers = getBeatMarkersArr(
      samplingRate,
      adcGain,
      expectedDiagnosisLeadChannelADC,
    );

    // * Set currentBeatMarkers to Pantompkin value if is not AI beat markers and has no value
    if (
      (typeof this.currentBeatMarkers === 'undefined' ||
        !this.currentBeatMarkers ||
        !this.currentBeatMarkers.length) &&
      !this.isAppliedAI
    ) {
      this.currentBeatMarkers = [...this.pantompkinBeatMarkers];
    }
  };

  AIECGChart.prototype.reCalculateSamples = function () {
    const { someChannelsHidden } = this.config;

    if (someChannelsHidden) {
      this.samples = this.displayedChannelsArr.map((chIdx: any) =>
        this.originalSamples[chIdx].slice(0),
      );

      this.filterSamples(false);

      this.config.numberOfVisibleChannels = this.samples.length;
    } else {
      this.filterSamples(true);

      this.config.numberOfVisibleChannels = this.samples.length;
    }

    this.calculateConfig();
    this.calculatePixelSamplesFromSamples(this.samples);
  };

  AIECGChart.prototype.reCalculatePixelSamples = function () {
    this.calculateConfig();

    const { maxIndex, minIndex, prevChanIdx } = this.config;

    this.calculatePixelSamplesFromSamples(this.samples);

    this.gridBackGround.update(this.config);
    this.exportGrid.update(this.config);
    this.ecgView.update(
      this.pixelSamples,
      this.config,
      this.displayedChannelsArr,
      this.verticalChannelPlacement,
    );
    this.markers.update(
      this.currentBeatMarkers,
      this.verticalChannelPlacement,
      this.config,
      this.isAppliedAI,
      this.isShowVES,
      this.isShowSVES,
      this.currentHesBeatStatus,
      this.aiData,
    );

    if (this.isDisplayScrollbar) {
      this.scroller.update(this.samplesScroller, this.config, prevChanIdx);
    }

    this.titleChart.update(
      this.verticalChannelPlacement,
      this.config,
      this.displayedChannelsArr,
      this.timeObject,
    );

    this.offsetX = this.offsetX < maxIndex ? this.offsetX : maxIndex;
    this.offsetX = this.offsetX > minIndex ? this.offsetX : minIndex;

    this.annotation.updateConfig(
      this.config,
      this.offsetX,
      this.verticalChannelPlacement,
      this.displayedChannelsArr,
    );

    this.loadFullECG(this.offsetX);
  };

  AIECGChart.prototype.assignFunction = function (functionObject: any) {
    this.propsFunction = {};
    this.propsFunction.updateSelectedBeatMarker = functionObject.updateSelectedBeatMarker;
    this.propsFunction.updatePrevChanIdx = functionObject.updatePrevChanIdx;
    this.propsFunction.updateVisibleChannels = functionObject.updateVisibleChannels;
    this.propsFunction.updateSpdMenu = functionObject.updateSpdMenu;
    this.propsFunction.updateAmpMenu = functionObject.updateAmpMenu;
    this.propsFunction.handleChangeHiLoPDropdown = functionObject.handleChangeHiLoPDropdown;
    this.propsFunction.toggleViewAllMode = functionObject.toggleViewAllMode;
    this.propsFunction.updateCompletedStatus = functionObject.updateCompletedStatus;
    this.propsFunction.updateWaitingZoomModeStatus = functionObject.updateWaitingZoomModeStatus;
    this.propsFunction.updateZoomModeStatus = functionObject.updateZoomModeStatus;
    this.propsFunction.updateARBeatModeStatus = functionObject.updateARBeatModeStatus;
    this.propsFunction.updateSelectingHLineStatus = functionObject.updateSelectingHLineStatus;
    this.propsFunction.updateSelectingVLineStatus = functionObject.updateSelectingVLineStatus;
    this.propsFunction.updateSelectingPatternStatus = functionObject.updateSelectingPatternStatus;
    this.propsFunction.handleOpenSnackbar = functionObject.handleOpenSnackbar;
    this.propsFunction.handlePickDate = functionObject.handlePickDate;
    this.propsFunction.handleUpdateBeat = functionObject.handleUpdateBeat;
    this.propsFunction.updateBeatTypeNumber = functionObject.updateBeatTypeNumber;
    this.propsFunction.updateSelectingBeatStatus = functionObject.updateSelectingBeatStatus;
    this.propsFunction.navigateMinute = functionObject.navigateMinute;
  };

  AIECGChart.prototype.bindEventListener = function () {
    // remove event to prevent duplicate listener
    window.removeEventListener('keydown', this.eventNavigateFrame, true);
    window.removeEventListener('keydown', this.eventARBeatMarker, true);
    window.removeEventListener('keyup', this.eventCancelDrawHLine);
    window.removeEventListener('keyup', this.eventCancelDrawVLine);
    window.removeEventListener('keyup', this.eventCancelDrawRectSelect);
    window.removeEventListener('keyup', this.eventCancelDrawText);
    window.removeEventListener('keyup', this.eventCancelZoom);
    window.removeEventListener('keyup', this.eventExitZoom);
    this.eventCancelDrawHLine = this.handleCancelDrawHLine.bind(this);
    this.eventCancelDrawVLine = this.handleCancelDrawVLine.bind(this);
    this.eventCancelDrawText = this.handleCancelDrawText.bind(this);
    this.eventARBeatMarker = this.handleEventARBeatMarker.bind(this);
    this.eventCancelZoom = this.handleCancelZoom.bind(this);
    this.eventExitZoom = this.handleExitZoom.bind(this);
    this.eventCancelDrawRectSelect = this.handleCancelDrawRectSelect.bind(this);
    this.eventNavigateFrame = this.handleNavigateFrame.bind(this);
  };

  AIECGChart.prototype.updateData = function ({
    rawDataFromNetwork = [[], [], []],
    config,
    beatMarkerArr = [],
    hesBeatStatus = [],
    annotations,
    isDisplayScrollbar,
    hiddenChannels = [0, 1, 2],
    events,
    missingSampleLength,
    isAppliedAI,
    isShowVES,
    isShowSVES,
    diagnosticLead,
    calculateBeatMarkerChannel,
    timeObject,
    ecgDataMap,
  }: any) {
    this.isActive = true;
    this.offsetX = 0;
    this.timeObject = timeObject;
    this.ecgDataMap = ecgDataMap;
    this.isAppliedAI = isAppliedAI;
    this.isShowVES = isShowVES;
    this.isShowSVES = isShowSVES;
    this.diagnosticLead = diagnosticLead;
    this.calculateBeatMarkerChannel = calculateBeatMarkerChannel;

    this.isInZoomMode = ECG_VIEWER_CONFIG_STORE.isInZoomMode;
    this.zoomingChannel = ECG_VIEWER_CONFIG_STORE.zoomingChannel;
    if (this.isInZoomMode) {
      this.lastPrevChanIdx = ECG_VIEWER_CONFIG_STORE.prevChanIdx;
    }
    this.beforeZoomValue = ECG_VIEWER_CONFIG_STORE.beforeZoomValue;

    this.isInViewAllMode = ECG_VIEWER_CONFIG_STORE.isInViewAllMode;
    this.beforeViewAllValue = ECG_VIEWER_CONFIG_STORE.beforeViewAllValue;

    this.isDisplayScrollbar = isDisplayScrollbar;
    this.annotation.deleteBeatIndicator();

    if (this.actionType !== ACTION_TYPE.AR_BEAT_MARKER) {
      this.removeAllEventListener();
      this.changeCursor(MOUSE_CURSOR.DEFAULT);
    }
    this.bindEventListener();
    this.initValue(
      rawDataFromNetwork,
      config,
      beatMarkerArr,
      hesBeatStatus,
      hiddenChannels,
      missingSampleLength,
    );
    window.addEventListener('keydown', this.eventNavigateFrame, true);
    // TODO: Should we use it?
    // this.isInViewAllMode = true;

    this.canvasBackground.clear();

    // *: Delete all annotations
    const annoObj: any[] = [];
    this.annotation.annoArr.forEach((anno: any) => {
      this.annotation.selectObjByInstance(anno);
      this.annotation.deleteObj(true);
      if (anno.type === ANNOTATION_TYPE.EVENT_POSITION) {
        annoObj.push(anno.rect, anno.text, anno.bottomRect);
      }
      if (anno.type === ANNOTATION_TYPE.EVENT_INDICATOR) {
        annoObj.push(anno.line, anno.rectWithText);
      }
      if (anno.type === ANNOTATION_TYPE.BOOKMARK_INDICATOR) {
        annoObj.push(anno.line, anno.polyline);
      }
    });
    this.canvasObject.remove(...annoObj);
    annoObj.length = 0;
    this.annotation.annoArr = [];

    if (this.annotation.selectedObj) {
      this.annotation.selectedObj = null;
    }

    this.ecgView.updateData();

    this.titleChart.updateData(config, this.timeObject);

    this.zoomingChannel = null;
    this.isDownLeft = false;
    this.isDownRight = false;
    if (this.actionType !== ACTION_TYPE.AR_BEAT_MARKER) {
      this.actionType = null;
    }
    this.isInWaitingZoomMode = false;

    if (this.isDisplayScrollbar) {
      this.isMouseDownScroller = false;
      this.isMouseOverScroller = false;
      this.isMouseUpScroller = false;
    }

    this.isDrawingHLine = false;
    this.isDrawingVLine = false;
    this.isDrawingRectSelect = false;
    this.isDrawingText = false;
    this.isDrawingBCC = false;

    this.propsFunction.updateSpdMenu(this.config.speed);
    this.propsFunction.updateAmpMenu(this.config.gain);
    this.propsFunction.handleChangeHiLoPDropdown(true, this.config.LFF);
    this.propsFunction.handleChangeHiLoPDropdown(false, this.config.HFF);
    this.updateConfig();

    if (!_.isEmpty(events)) {
      this.events = events;
      this.drawEventsPosition(events, true);
    }

    const { jumpOffset, beatPos, samplingRate, sampleLength, sampleRange } = this.config;

    if (!_.isNil(beatPos)) {
      this.drawBeatPosition(beatPos, true);
    }

    if (jumpOffset) {
      this.offsetX = jumpOffset;
      if (this.offsetX >= sampleLength - sampleRange) {
        this.offsetX = sampleLength - sampleRange;
      }
      if (this.offsetX < 0) {
        this.offsetX = 0;
      }
      this.annotation.scroll(this.offsetX, this.displayedChannelsArr);
      this.loadECG(this.offsetX);
    }

    if (annotations && !_.isEmpty(annotations)) {
      this.importJSONObject(annotations);
    }
  };

  AIECGChart.prototype.updateConfig = function () {
    this.reCalculateSamples();

    const { maxIndex, minIndex, prevChanIdx, isShowBeatMarkers } = this.config;

    // * Update grid after exit View All Mode
    if (!this.isInViewAllMode) {
      this.gridBackGround.update(this.config);
      this.exportGrid.update(this.config);
    }

    this.ecgView.update(
      this.pixelSamples,
      this.config,
      this.displayedChannelsArr,
      this.verticalChannelPlacement,
    );
    this.markers.update(
      this.currentBeatMarkers,
      this.verticalChannelPlacement,
      this.config,
      this.isAppliedAI,
      this.isShowVES,
      this.isShowSVES,
      this.currentHesBeatStatus,
      this.aiData,
    );

    if (this.isDisplayScrollbar) {
      this.scroller.update(this.samplesScroller, this.config, prevChanIdx);
    }

    this.titleChart.update(
      this.verticalChannelPlacement,
      this.config,
      this.displayedChannelsArr,
      this.timeObject,
    );

    // * Re-calculate offsetX depend on clickedOffsetX after exit View All Mode
    if (this.isClickedViewAll) {
      this.isClickedViewAll = false;
      this.offsetX = this.clickedOffsetX - Math.round(this.config.sampleRange / 2);
    }

    this.offsetX = this.offsetX < maxIndex ? this.offsetX : maxIndex;
    this.offsetX = this.offsetX > minIndex ? this.offsetX : minIndex;

    this.annotation.updateConfig(
      this.config,
      this.offsetX,
      this.verticalChannelPlacement,
      this.displayedChannelsArr,
    );

    // * Show all beat markers before exporting image
    if (this.config.speed === 2.5 || this.isInViewAllMode) {
      this.showHideBeatMarkers(this.isExporting);
    } else {
      this.showHideBeatMarkers(isShowBeatMarkers);
    }

    this.loadFullECG(this.offsetX);
  };

  AIECGChart.prototype.showHideBeatMarkers = function (
    isShowBeatMarkers: any,
    shouldUpdateConfig: any = false,
  ) {
    if (shouldUpdateConfig) {
      this.config.isShowBeatMarkers = isShowBeatMarkers;
    }
    this.markers.setBeatMarkersVisibility(isShowBeatMarkers);
    this.renderECG();
  };

  AIECGChart.prototype.removeAllEventListener = function () {
    window.removeEventListener('keydown', this.eventARBeatMarker, true);
    window.removeEventListener('keyup', this.eventCancelDrawHLine);
    window.removeEventListener('keyup', this.eventCancelDrawVLine);
    window.removeEventListener('keyup', this.eventCancelDrawRectSelect);
    window.removeEventListener('keyup', this.eventCancelDrawText);
    window.removeEventListener('keyup', this.eventCancelZoom);
    window.removeEventListener('keyup', this.eventExitZoom);
    this.eventARBeatMarker = null;
    this.eventCancelDrawHLine = null;
    this.eventCancelDrawVLine = null;
    this.eventCancelDrawRectSelect = null;
    this.eventCancelDrawText = null;
    this.eventCancelZoom = null;
    this.eventExitZoom = null;
    if (this.scrollInterval) {
      clearInterval(this.scrollInterval);
      this.scrollInterval = null;
    }
  };

  AIECGChart.prototype.getSelectingStatus = function () {
    return this.annotation.selectedObj || this.ecgView.specifyPattern;
  };

  AIECGChart.prototype.getWidgets = function () {
    const annoArr = this.annotation.getJSONArr();
    const viewArr = this.ecgView.getJSONObject();
    return [...annoArr, ...viewArr];
  };

  AIECGChart.prototype.getBeatMarkers = function () {
    return this.markers.getBeatMarkers();
  };

  AIECGChart.prototype.getHesBeatStatus = function () {
    return this.markers.getHesBeatStatus();
  };

  AIECGChart.prototype.getWndDuration = function () {
    return this.config.wndDuration;
  };

  AIECGChart.prototype.toggleVES = function () {
    this.isShowVES = !this.isShowVES;
    this.markers.toggleVES(this.offsetX, this.isInViewAllMode);
  };

  AIECGChart.prototype.toggleSVES = function () {
    this.isShowSVES = !this.isShowSVES;
    this.markers.toggleSVES(this.offsetX, this.isInViewAllMode);
  };

  AIECGChart.prototype.deleteObject = function () {
    if (
      this.ecgView.specifySampleMark ||
      this.annotation.selectedObj ||
      this.ecgView.specifyPattern
    ) {
      if (this.ecgView.specifySampleMark || this.ecgView.specifyPattern) {
        // *: Delete sample mark or pattern
        this.ecgView.deleteMark();
        if (this.ecgView.specifyPattern) {
          this.propsFunction.updateSelectingPatternStatus(false);
        }
      }

      if (this.annotation.selectedObj) {
        // *: Delete object
        if (this.annotation.selectedObj.type === 'HorizontalCaliper') {
          this.propsFunction.updateSelectingHLineStatus(false);
        } else if (this.annotation.selectedObj.type === ANNOTATION_TYPE.VLINE) {
          this.propsFunction.updateSelectingVLineStatus(false);
        }
        this.annotation.deleteObj();
      }

      this.actionType = null;
      this.loadECG(this.offsetX);
    }
  };

  AIECGChart.prototype.bringEventIndicatorToFront = function () {
    if (this.annotation) {
      this.annotation.annoArr.forEach((x: any) => {
        if (x.type === ANNOTATION_TYPE.EVENT_INDICATOR) {
          this.canvasObject.bringToFront(x.rectWithText);
        }
      });
    }
  };

  AIECGChart.prototype.handleCallbackUpdateBeat = function () {
    this.bringEventIndicatorToFront();
    this.currentBeatMarkers = this.markers.getBeatMarkers();
    this.currentHesBeatStatus = this.markers.getHesBeatStatus();
    this.propsFunction.handleUpdateBeat({
      currentBeatMarkers: this.currentBeatMarkers,
      currentHesBeatStatus: this.currentHesBeatStatus,
    });
  };

  AIECGChart.prototype.handleUpdateBelowText = function () {
    // *: Update belowText after adjusting beat markers
    if (this.annotation.annoArr && this.annotation.annoArr.length) {
      const beatMarkersUpdate = this.annotation.annoArr.filter(
        (x: any) => x.type === 'HorizontalCaliper',
      );
      if (beatMarkersUpdate && beatMarkersUpdate.length) {
        beatMarkersUpdate.forEach((beatMarkerUpdate: any) => {
          beatMarkerUpdate.updateBelowText(this.markers.beatMarkers, this.offsetX);
        });
      }
    }
  };

  AIECGChart.prototype.drawBeatPosition = function (pos: any, isScroll: any) {
    this.annotation.drawBeatPosition(pos, this.offsetX);

    // *: Jump to the Event position
    if (isScroll) {
      const { samplingRate, wndDuration, width, sampleLength } = this.config;
      const halfWindow = Math.round(((width / 2) * samplingRate * wndDuration) / width);
      if (pos <= halfWindow) {
        this.offsetX = 0;
      } else if (pos >= sampleLength - halfWindow) {
        this.offsetX = sampleLength - 2 * halfWindow;
      } else {
        this.offsetX = pos - halfWindow;
      }
      if (this.offsetX < 0) {
        this.offsetX = 0;
      }
    }
    this.annotation.scroll(this.offsetX, this.displayedChannelsArr);
    this.loadECG(this.offsetX);
  };

  AIECGChart.prototype.drawEventsPosition = function (events: any, isScroll: any) {
    this.annotation.drawEventsPosition(events, this.offsetX);
    // *: Jump to the Event position
    if (isScroll) {
      const selectedEvent = _.find(events, (event: any) => event.isSelected) ?? events[0];
      // if has (color: any) => is (event: any) => jump to
      if (selectedEvent.color) {
        const selectedEventPos = selectedEvent.startPos;
        const { samplingRate, wndDuration, width, sampleLength, sampleRange } = this.config;
        const portion = selectedEvent.portion ?? 10;
        if (portion === 0) {
          this.offsetX = selectedEventPos - 3;
        } else {
          const windowPos = Math.round(((width / portion) * samplingRate * wndDuration) / width);
          this.offsetX = selectedEventPos - windowPos;
        }
        if (this.offsetX >= sampleLength - sampleRange) {
          this.offsetX = sampleLength - sampleRange;
        }
        if (this.offsetX < 0) {
          this.offsetX = 0;
        }
      }
    }
    this.annotation.scroll(this.offsetX, this.displayedChannelsArr);
    this.loadECG(this.offsetX);
  };

  AIECGChart.prototype.handleScrollOnMove = function (movementX: any) {
    const { maxIndex, minIndex, sampleRange, width } = this.config;

    this.offsetX -= Math.round((movementX * sampleRange) / width);
    this.offsetX = this.offsetX < maxIndex ? this.offsetX : maxIndex;
    this.offsetX = this.offsetX > minIndex ? this.offsetX : minIndex;

    this.annotation.scroll(this.offsetX, this.displayedChannelsArr);
    this.loadECG(this.offsetX);
    if (this.actionType === ACTION_TYPE.AR_BEAT_MARKER) {
      this.markers.resetHoverBeat();
      if (this.isDrawingBeatSelect) {
        this.annotation.scrollBeatSelection(this.offsetX);
        const lastPointOnMoveSample =
          Math.round(this.lastPointOnMove.x / this.config.indexToPix) + this.offsetX;
        const startSample = Math.min(this.contextMenuSample, lastPointOnMoveSample);
        const endSample = Math.max(this.contextMenuSample, lastPointOnMoveSample);
        this.markers.handleSelectBeat({
          startSample,
          endSample,
          beatTypeNumber: this.beatTypeNumber,
        });
      }
    }
  };

  AIECGChart.prototype.changeCursor = function (type: any) {
    if (this.canvasObject) {
      this.canvasObject.defaultCursor = type;
      this.canvasObject.hoverCursor = type;
    }
  };

  AIECGChart.prototype.setConfig = function (configObject: any) {
    const {
      speedSetConfig,
      gainSetConfig,
      wndDurationSetConfig,
      majorDivSizeSetConfig,
      fineGridSetConfig,
      numberOfVisibleChannelsSetConfig,
      displayedChannelsArrSetConfig,
    } = configObject;
    this.config.speed = speedSetConfig;
    this.config.gain = gainSetConfig;
    this.config.wndDuration = wndDurationSetConfig;
    this.config.majorDivSize = majorDivSizeSetConfig;
    this.config.fineGrid = fineGridSetConfig;
    this.config.numberOfVisibleChannels = numberOfVisibleChannelsSetConfig;
    this.config.someChannelsHidden = numberOfVisibleChannelsSetConfig.length !== 3;
    this.displayedChannelsArr = displayedChannelsArrSetConfig;
    this.updateConfig();
  };

  AIECGChart.prototype.updateSpeed = function (newSpeed: any) {
    const changeProportion = this.config.speed / newSpeed;
    this.config.speed = newSpeed;
    this.config.wndDuration *= changeProportion;

    // *: Cuong test: view all
    // this.config.defaultSpeed = this.config.speed;
    // this.config.defaultWndDuration = this.config.wndDuration;

    if (this.isInWaitingZoomMode) {
      this.startZoom(true);
    }

    this.updateConfig();

    const { beatPos } = this.config;

    if (!_.isNil(beatPos)) {
      this.drawBeatPosition(beatPos, true);
    }
  };

  AIECGChart.prototype.updateGain = function (newGain: any) {
    this.config.gain = newGain;

    if (this.isInWaitingZoomMode) {
      this.startZoom(true);
    }

    this.updateConfig();
  };

  AIECGChart.prototype.updateHighLowPass = function (isHighPass: any, freq: any) {
    const { samplingRate } = this.config;

    if (isHighPass) {
      this.config.LFF = freq;
    } else {
      this.config.HFF = freq;
    }

    this.samples = this.displayedChannelsArr.map((chIdx: any) =>
      this.originalSamples[chIdx].slice(0),
    ); // copy original samples

    if (this.isDisplayScrollbar) {
      this.samplesScroller = this.originalSamples.slice(0);
      const samplesScrollerLength = this.samplesScroller.length;
      for (let i = 0; i < samplesScrollerLength; i += 1) {
        this.samplesScroller[i] = Highpass(this.samplesScroller[i], this.config.LFF, samplingRate);
        this.samplesScroller[i] = Lowpass(this.samplesScroller[i], this.config.HFF, samplingRate);
      }
    }
    const samplesLength = this.samples.length;
    for (let i = 0; i < samplesLength; i += 1) {
      this.samples[i] = Highpass(this.samples[i], this.config.LFF, samplingRate);
      this.samples[i] = Lowpass(this.samples[i], this.config.HFF, samplingRate);
    }

    this.reCalculatePixelSamples();
  };

  AIECGChart.prototype.updatePrevChanIdx = function (prevChanIdx: any) {
    this.config.prevChanIdx = prevChanIdx;

    this.scroller.updatePrevChanIdx(this.samplesScroller, this.offsetX, prevChanIdx);
  };

  AIECGChart.prototype.saveContextMenuPosition = function (point: any) {
    const { indexToPix } = this.config;

    this.contextMenuPoint = point;
    this.contextMenuSample = Math.round(point.x / indexToPix) + this.offsetX;
  };

  AIECGChart.prototype.exitAllMode = function () {
    if (this.isDrawingHLine) {
      if (this.annotation.selectedObj) {
        this.changeCursor(MOUSE_CURSOR.DEFAULT);
        this.annotation.deleteObj(false);
        this.isDrawingHLine = false;
        this.actionType = null;
        this.isScrollingChart = false;
        this.isDownLeft = false;
        this.isDownRight = false;
        this.haveEntered = false;
        this.scrollDirection = null;
        this.lastPointOnMove = null;
        this.pointOnMove = null;
        this.renderECG();
        clearInterval(this.scrollInterval);
      }
      window.removeEventListener('keyup', this.eventCancelDrawHLine);
      this.propsFunction.updateCompletedStatus(false, false);
    }
    if (this.isDrawingVLine) {
      if (this.annotation.selectedObj) {
        this.changeCursor(MOUSE_CURSOR.DEFAULT);
        this.annotation.deleteObj(false);
        this.isDrawingVLine = false;
        this.actionType = null;
        this.isScrollingChart = false;
        this.isDownLeft = false;
        this.isDownRight = false;
        this.haveEntered = false;
        this.scrollDirection = null;
        this.lastPointOnMove = null;
        this.pointOnMove = null;
        this.renderECG();
      }
      window.removeEventListener('keyup', this.eventCancelDrawVLine);
      this.propsFunction.updateCompletedStatus(false, false);
    }
    if (this.isDrawingText) {
      if (
        this.annotation.selectedObj &&
        this.annotation.selectedObj.isDrawing &&
        this.isDrawingText
      ) {
        this.annotation.deleteObj(false);
        this.actionType = null;
        this.isDrawingText = false;
        this.renderECG();
        this.propsFunction.updateCompletedStatus(false, false);
      }
      window.removeEventListener('keyup', this.eventCancelDrawText);
      this.propsFunction.updateCompletedStatus(false, false);
    }
    if (this.actionType === ACTION_TYPE.AR_BEAT_MARKER) {
      this.exitARBeatMode();
      this.propsFunction.updateCompletedStatus(false, false);
    }
    if (this.isDrawingRectSelect) {
      this.annotation.deleteObj(false);
      this.actionType = null;
      this.isDrawingRectSelect = false;
      this.isScrollingChart = false;
      this.isDownRight = false;
      this.isDownLeft = false;
      this.haveEntered = false;
      this.scrollDirection = null;
      this.lastPointOnMove = null;
      this.pointOnMove = null;
      this.renderECG();
      clearInterval(this.scrollInterval);
      window.removeEventListener('keyup', this.eventCancelDrawRectSelect);
      this.propsFunction.updateCompletedStatus(false, false);
    }
    if (
      !(this.actionType === ACTION_TYPE.AR_BEAT_MARKER) &&
      !this.isInWaitingZoomMode &&
      !this.isDrawingHLine &&
      !this.isDrawingVLine &&
      !this.isDrawingRectSelect
    ) {
      this.exitZoom();
    }
    if (this.isInViewAllMode) {
      this.propsFunction.toggleViewAllMode();
      this.exitViewAllMode();
    }
  };

  AIECGChart.prototype.enterPickDate = function (pickDateAction: any) {
    this.exitAllMode();
    this.changeCursor(MOUSE_CURSOR.POINTER);
    this.isPickingDate = true;
    this.annotation.drawPickDateIndicator(pickDateAction);
    this.propsFunction.handleOpenSnackbar(
      `Select ${_.capitalize(pickDateAction)} time, press ESC to cancel`,
    );
  };

  AIECGChart.prototype.exitPickDate = function () {
    this.changeCursor(MOUSE_CURSOR.DEFAULT);
    this.isPickingDate = false;
    this.annotation.deletePickDateIndicator();
  };

  AIECGChart.prototype.drawAnnotation = function (type: any) {
    const { sampleLength } = this.config;

    if (this.contextMenuSample <= sampleLength) {
      this.actionType = type;

      const clickedChannelIndex = getClosestChannelIndex(
        this.contextMenuPoint.y,
        this.verticalChannelPlacement,
      );

      switch (type) {
        case ANNOTATION_TYPE.HLINE: {
          this.changeCursor(MOUSE_CURSOR.CROSSHAIR);
          this.isDrawingHLine = true;
          this.annotation.draw(
            this.offsetX,
            clickedChannelIndex,
            ANNOTATION_TYPE.HLINE,
            this.contextMenuPoint,
          );
          this.annotation.updateChannelIndex(this.displayedChannelsArr);
          window.addEventListener('keyup', this.eventCancelDrawHLine);
          break;
        }

        case ANNOTATION_TYPE.VLINE: {
          this.changeCursor(MOUSE_CURSOR.CROSSHAIR);
          this.isDrawingVLine = true;
          this.annotation.draw(
            this.offsetX,
            clickedChannelIndex,
            ANNOTATION_TYPE.VLINE,
            this.contextMenuPoint,
          );
          this.annotation.updateChannelIndex(this.displayedChannelsArr);
          window.addEventListener('keyup', this.eventCancelDrawVLine);
          break;
        }

        case ANNOTATION_TYPE.RECT_SELECT: {
          this.isDrawingRectSelect = true;
          this.annotation.draw(
            this.offsetX,
            clickedChannelIndex,
            ANNOTATION_TYPE.RECT_SELECT,
            this.contextMenuPoint,
          );
          this.annotation.updateChannelIndex(this.displayedChannelsArr);
          window.addEventListener('keyup', this.eventCancelDrawRectSelect);
          break;
        }

        case ANNOTATION_TYPE.TEXT: {
          this.actionType = null;
          this.isDrawingText = true;
          this.annotation.draw(
            this.offsetX,
            clickedChannelIndex,
            ANNOTATION_TYPE.TEXT,
            this.contextMenuPoint,
          );
          this.annotation.updateChannelIndex(this.displayedChannelsArr);
          window.addEventListener('keyup', this.eventCancelDrawText);
          break;
        }

        case ANNOTATION_TYPE.BCC: {
          this.actionType = null;

          const bccObj = getPQSTObject(
            2500,
            this.contextMenuPoint.x,
            this.displayedChannelsArr[clickedChannelIndex],
            {
              config: this.config,
              ECGBuffer: this.rawDataZoom,
              ANNBuffer: this.currentBeatMarkers,
              offsetX: this.offsetX,
              isReturnRealValue: !(this.rawDataZoom && this.rawDataZoom.length),
            },
          );

          this.isDrawingBCC = true;

          // *: In case of bccObj is null
          if (!bccObj.P || !bccObj.Q || !bccObj.R || !bccObj.S || !bccObj.T || !bccObj.R2) {
            let r2Manual = null;
            let pManual = null;
            let tManual = null;
            let qManual = null;
            let sManual = null;
            let rManual = this.currentBeatMarkers.find((x: any) => x >= this.contextMenuSample);
            if (rManual) {
              const rIndexManual = this.currentBeatMarkers.findIndex((x: any) => x === rManual);

              if (rIndexManual === 0) {
                r2Manual = this.currentBeatMarkers[rIndexManual + 1];
                sManual = rManual + (r2Manual - rManual) / 3;
                tManual = rManual + ((r2Manual - rManual) / 3) * 2;
                pManual = r2Manual + 60;
                qManual = pManual + 60;
              } else if (rIndexManual === this.currentBeatMarkers.length - 1) {
                rManual = this.contextMenuSample;
                r2Manual = rManual - 60;
                tManual = r2Manual - 60;
                sManual = tManual - 60;
                qManual = sManual - 60;
                pManual = qManual - 60;
              } else {
                r2Manual = this.currentBeatMarkers[rIndexManual + 1];
                pManual = rManual - 60;
                qManual = rManual - 30;
                sManual = rManual + (r2Manual - rManual) / 3;
                tManual = rManual + ((r2Manual - rManual) / 3) * 2;
              }
            } else {
              rManual = this.contextMenuSample;
              r2Manual = rManual - 60;
              tManual = r2Manual - 60;
              sManual = tManual - 60;
              qManual = sManual - 60;
              pManual = qManual - 60;
            }

            _.assign(bccObj, {
              P: pManual,
              Q: qManual,
              R: rManual,
              R2: r2Manual,
              S: sManual,
              T: tManual,
            });
          }

          this.annotation.draw(
            this.offsetX,
            this.displayedChannelsArr[clickedChannelIndex],
            ANNOTATION_TYPE.BCC,
            this.contextMenuPoint,
            bccObj,
          );

          this.startZoom(true, { speed: 100, gain: 50 });
          this.zoom(clickedChannelIndex, this.contextMenuPoint.x);

          break;
        }

        default: {
          break;
        }
      }
    } else {
      toastrError('Error');
    }
  };

  AIECGChart.prototype.changeBeatMarkerType = function (
    selectedBeatMarker: any,
    selectedBeatMarkerIndex: any,
    beatTypeNumber: any,
  ) {
    this.markers.changeBeatType(
      selectedBeatMarker,
      selectedBeatMarkerIndex,
      beatTypeNumber,
      this.offsetX,
    );
    this.handleUpdateBelowText();
    this.handleCallbackUpdateBeat();
  };

  AIECGChart.prototype.deleteBeatMarker = function (
    selectedBeatMarker: any,
    selectedBeatMarkerIndex: any,
  ) {
    this.markers.deleteBeatByObject(selectedBeatMarker, selectedBeatMarkerIndex, this.offsetX);
    this.handleUpdateBelowText();
    this.handleCallbackUpdateBeat();
  };

  AIECGChart.prototype.handleDrawMouseOver = function () {
    if (this.actionType === ACTION_TYPE.AR_BEAT_MARKER) {
      this.annotation.updateBeatIndicator({ visible: true });
    }
    if (this.isPickingDate) {
      this.annotation.updatePickDateIndicator({ visible: true });
    }
    if (this.isDrawingHLine || this.isDrawingRectSelect || this.isDrawingBeatSelect) {
      this.isScrollingChart = false;
      this.haveEntered = true;
      clearInterval(this.scrollInterval);
      this.scrollInterval = null;
    }
  };

  AIECGChart.prototype.handleDrawMouseOut = function (props: any) {
    if (this.actionType === ACTION_TYPE.AR_BEAT_MARKER) {
      this.markers.resetHoverBeat();
      this.annotation.updateBeatIndicator({ visible: false });
    }
    if (this.isPickingDate) {
      this.annotation.updatePickDateIndicator({ visible: false });
    }
    if (this.isDrawingHLine || this.isDrawingRectSelect || this.isDrawingBeatSelect) {
      this.lastPointOnMove = this.pointOnMove;
      this.scrollDirection = null;
      this.isScrollingChart = true;

      if (this.isScrollingChart && props.layerY >= 0 && props.layerY <= 245) {
        this.scrollDirection = props.offsetX < this.contextMenuPoint.x ? 'left' : 'right';

        const movementX =
          this.config.speed < 500
            ? this.scrollDirection === 'right'
              ? -2
              : 2
            : this.scrollDirection === 'right'
              ? -5
              : 5;
        const leValue = this.scrollDirection === 'right' ? this.config.width : 0;

        this.scrollInterval = setInterval(() => {
          this.annotation.prevPoint = { ...this.lastPointOnMove };
          this.lastPointOnMove.x -= movementX;
          this.handleScrollOnMove(movementX);
          this.annotation.update(
            this.lastPointOnMove,
            true,
            this.displayedChannelsArr,
            true,
            leValue,
            this.markers.beatMarkers,
            this.offsetX,
          );
          if (this.isDrawingBeatSelect) {
            this.annotation.updateBeatSelection(
              this.lastPointOnMove,
              true,
              leValue,
              isDeleteBeatMode(this.beatTypeNumber),
            );
          }
        }, 1);
      }
    }
  };

  AIECGChart.prototype.changeHLineColor = function (color: any) {
    if (this.annotation.selectedObj && this.annotation.selectedObj.type === 'HorizontalCaliper') {
      this.annotation.selectedObj.changeColor(color);
      this.renderECG();
    }
  };

  AIECGChart.prototype.changeVLineColor = function (color: any) {
    if (this.annotation.selectedObj && this.annotation.selectedObj.type === ANNOTATION_TYPE.VLINE) {
      this.annotation.selectedObj.changeColor(color);
      this.renderECG();
    }
  };

  AIECGChart.prototype.getLineColor = function () {
    if (this.annotation.selectedObj && this.annotation.selectedObj.type === ANNOTATION_TYPE.HLINE) {
      return this.annotation.selectedObj.colorHLine;
    }
    if (this.annotation.selectedObj && this.annotation.selectedObj.type === ANNOTATION_TYPE.VLINE) {
      return this.annotation.selectedObj.colorVLine;
    }
    if (this.ecgView.specifyPattern) {
      return this.ecgView.specifyPattern.color;
    }
    return '';
  };

  AIECGChart.prototype.changePatternColor = function (color: any) {
    this.ecgView.changePatternColor(color, this.offsetX);
  };

  AIECGChart.prototype.handleCancelDrawHLine = function (event: any) {
    const { keyCode } = event;
    if (keyCode === 27) {
      if (this.annotation.selectedObj) {
        this.changeCursor(MOUSE_CURSOR.DEFAULT);
        this.annotation.deleteObj(false);
        this.isDrawingHLine = false;
        this.actionType = null;
        this.isScrollingChart = false;
        this.isDownLeft = false;
        this.isDownRight = false;
        this.haveEntered = false;
        this.scrollDirection = null;
        this.lastPointOnMove = null;
        this.pointOnMove = null;
        this.renderECG();
        clearInterval(this.scrollInterval);
      }
      window.removeEventListener('keyup', this.eventCancelDrawHLine);
      this.propsFunction.updateCompletedStatus(false, false);
    }
  };

  AIECGChart.prototype.handleCancelDrawVLine = function (event: any) {
    const { keyCode } = event;
    if (keyCode === 27) {
      if (this.annotation.selectedObj) {
        this.changeCursor(MOUSE_CURSOR.DEFAULT);
        this.annotation.deleteObj(false);
        this.isDrawingVLine = false;
        this.actionType = null;
        this.isScrollingChart = false;
        this.isDownLeft = false;
        this.isDownRight = false;
        this.haveEntered = false;
        this.scrollDirection = null;
        this.lastPointOnMove = null;
        this.pointOnMove = null;
        this.renderECG();
      }
      window.removeEventListener('keyup', this.eventCancelDrawVLine);
      this.propsFunction.updateCompletedStatus(false, false);
    }
  };

  AIECGChart.prototype.handleCancelDrawText = function (event: any) {
    const { keyCode } = event;
    if (keyCode === 27) {
      if (
        this.annotation.selectedObj &&
        this.annotation.selectedObj.isDrawing &&
        this.isDrawingText
      ) {
        this.annotation.deleteObj(false);
        this.actionType = null;
        this.isDrawingText = false;
        this.renderECG();
        this.propsFunction.updateCompletedStatus(false, false);
      }
      window.removeEventListener('keyup', this.eventCancelDrawText);
      this.propsFunction.updateCompletedStatus(false, false);
    }
  };

  AIECGChart.prototype.handleNavigateFrame = function (event: any) {
    if (event?.repeat || !this.isDisplayScrollbar) {
      return;
    }
    const tabZone = document.getElementById(this.idComponents.tab);
    // *:Active key press when hover in tab zone
    if (tabZone && !tabZone.matches(':hover')) {
      return;
    }
    const { keyCode, ctrlKey, metaKey } = event;
    if (ctrlKey || metaKey) {
      return;
    }
    const { sampleLength, sampleRange } = this.config;
    switch (keyCode) {
      case 188: {
        // ,<
        const newOffset = this.offsetX - sampleRange;
        if (this.offsetX === 0 || this.offsetX === 1) {
          this.propsFunction.navigateMinute();
          break;
        }
        this.offsetX = newOffset < 0 ? 0 : newOffset;
        this.annotation.scroll(this.offsetX, this.displayedChannelsArr);
        this.loadECG(this.offsetX);
        break;
      }
      case 190: {
        // .>
        const newOffset = this.offsetX + sampleRange;
        if (this.offsetX === sampleLength - sampleRange) {
          this.propsFunction.navigateMinute(true);
          break;
        }
        this.offsetX =
          newOffset >= sampleLength - sampleRange ? sampleLength - sampleRange : newOffset;
        this.annotation.scroll(this.offsetX, this.displayedChannelsArr);
        this.loadECG(this.offsetX);
        break;
      }
      default: {
        break;
      }
    }
  };

  AIECGChart.prototype.handleEventARBeatMarker = function (event: any) {
    if (event?.repeat) {
      return;
    }
    const canvasObject = document.getElementsByClassName(
      `canvas-object-${this.idComponents.canvasObject}`,
    )[0];
    if (canvasObject && !canvasObject.matches(':hover')) {
      return;
    }
    const { keyCode, ctrlKey, metaKey } = event;
    if (ctrlKey || metaKey) {
      return;
    }
    switch (keyCode) {
      case 66: {
        // B
        event.stopImmediatePropagation();
        this.exitARBeatMode();
        this.propsFunction.updateCompletedStatus(false, false);
        break;
      }
      case 27: {
        // Esc
        event.stopImmediatePropagation();
        this.exitARBeatMode();
        this.propsFunction.updateCompletedStatus(false, false);
        break;
      }
      case 78: {
        // N
        if (this.isDrawingBeatSelect) return;
        event.stopImmediatePropagation();
        this.enterARBeatMode(1);
        break;
      }
      case 83: {
        // S
        if (this.isDrawingBeatSelect) return;
        event.stopImmediatePropagation();
        this.enterARBeatMode(70);
        break;
      }
      case 86: {
        // V
        if (this.isDrawingBeatSelect) return;
        event.stopImmediatePropagation();
        this.enterARBeatMode(60);
        break;
      }
      case 81: {
        // Q
        if (this.isDrawingBeatSelect) return;
        event.stopImmediatePropagation();
        this.enterARBeatMode(80);
        break;
      }
      case 68: {
        // Delete (D)
        if (this.isDrawingBeatSelect) return;
        event.stopImmediatePropagation();
        this.enterARBeatMode(1000);
        break;
      }
      default: {
        break;
      }
    }
  };

  AIECGChart.prototype.handleCancelDrawRectSelect = function (event: any) {
    const { keyCode } = event;
    if (keyCode === 27) {
      this.annotation.deleteObj(false);
      this.actionType = null;
      this.isDrawingRectSelect = false;
      this.isScrollingChart = false;
      this.isDownRight = false;
      this.isDownLeft = false;
      this.haveEntered = false;
      this.scrollDirection = null;
      this.lastPointOnMove = null;
      this.pointOnMove = null;
      this.renderECG();
      clearInterval(this.scrollInterval);
      window.removeEventListener('keyup', this.eventCancelDrawRectSelect);
      this.propsFunction.updateCompletedStatus(false, false);
    }
  };

  AIECGChart.prototype.handleCancelZoom = function (event: any) {
    const { keyCode } = event;
    if (keyCode === 27) {
      this.changeCursor(MOUSE_CURSOR.DEFAULT);
      this.actionType = null;
      this.beforeZoomValue = null;
      ECG_VIEWER_CONFIG_STORE.beforeZoomValue = this.beforeZoomValue;
      this.isInWaitingZoomMode = false;
      this.propsFunction.updateWaitingZoomModeStatus(false);
      this.propsFunction.updateCompletedStatus(false, false);
      window.removeEventListener('keyup', this.eventCancelZoom);
    }
  };

  AIECGChart.prototype.handleExitZoom = function (event: any) {
    const { keyCode } = event;
    if (keyCode === 27) {
      if (
        !(this.actionType === ACTION_TYPE.AR_BEAT_MARKER) &&
        !this.isInWaitingZoomMode &&
        !this.isDrawingHLine &&
        !this.isDrawingVLine &&
        !this.isDrawingRectSelect
      ) {
        this.exitZoom();
      }
    }
  };

  AIECGChart.prototype.markASample = function (seconds: any) {
    const { sampleLength } = this.config;
    if (this.contextMenuSample <= sampleLength) {
      const { samplingRate } = this.config;

      const clickedChannelIndex = getClosestChannelIndex(
        this.contextMenuPoint.y,
        this.verticalChannelPlacement,
      );
      const sampleMarkRange = samplingRate * seconds;
      const sampleMark = {
        channelIndex: this.displayedChannelsArr[clickedChannelIndex],
        startSample: this.clickedOffsetX - sampleMarkRange / 2,
        endSample: this.clickedOffsetX + sampleMarkRange / 2 - 1,
        sampleMarkRange,
      };

      this.ecgView.drawSampleMark(this.offsetX, sampleMark);
      this.propsFunction.updateCompletedStatus(false, true);
    } else {
      toastrError('Error');
    }
  };

  AIECGChart.prototype.getVisibleChannels = function () {
    return [...this.displayedChannelsArr];
  };

  AIECGChart.prototype.setVisibleChannels = function (displayedChannelsArr: any) {
    this.displayedChannelsArr = displayedChannelsArr.sort();

    if (!this.isInZoomMode) {
      this.exportDisplayedChannelsArr = this.displayedChannelsArr;
    }

    this.config.someChannelsHidden =
      this.displayedChannelsArr.length !== this.originalSamples.length;

    // TODO: De-select annotation if its channel is hidden (still error with Text & BCC)
    if (this.annotation.selectedObj) {
      this.annotation.selectedObj.deselect();
      this.annotation.selectedObj = null;
      // this.annotation.isModifying = false;
    }

    // *: De-select sample mark if its channel is hidden
    if (this.ecgView.specifySampleMark) {
      this.ecgView.specifySampleMark = null;
    }

    this.updateConfig();

    if (this.isInWaitingZoomMode) {
      this.startZoom(true);
    }
  };

  AIECGChart.prototype.filterSamples = function (reset: any) {
    const { LFF, HFF, samplingRate } = this.config;

    // *: Restore samples from originalSamples
    if (reset) {
      this.samples = this.originalSamples.map((chArr: any) => chArr.slice(0));

      if (this.isDisplayScrollbar) {
        this.samplesScroller = this.originalSamples.slice(0);
      }
    }
    const samplesLength = this.samples.length;
    for (let i = 0; i < samplesLength; i += 1) {
      this.samples[i] = Highpass(this.samples[i], LFF, samplingRate);
      this.samples[i] = Lowpass(this.samples[i], HFF, samplingRate);
    }

    if (this.isDisplayScrollbar) {
      const samplesScrollerLength = this.samplesScroller.length;
      for (let i = 0; i < samplesScrollerLength; i += 1) {
        this.samplesScroller[i] = Highpass(this.samplesScroller[i], LFF, samplingRate);
        this.samplesScroller[i] = Lowpass(this.samplesScroller[i], HFF, samplingRate);
      }
    }
  };

  AIECGChart.prototype.toggleFineGrid = function () {
    this.config.fineGrid = !this.config.fineGrid;

    this.gridBackGround.update(this.config);
    this.exportGrid.update(this.config);
    this.gridBackGround.draw();
    // this.exportGrid.drawExportGrid();
  };

  AIECGChart.prototype.enterARBeatMode = function (beatTypeNumber: any) {
    this.actionType = ACTION_TYPE.AR_BEAT_MARKER;
    this.beatTypeNumber = beatTypeNumber || ECG_VIEWER_CONFIG_STORE.beatTypeNumber;
    setTimeout(() => {
      this.changeCursor(
        isDeleteBeatMode(this.beatTypeNumber)
          ? MOUSE_CURSOR.DELETE_BEAT_CURSOR
          : MOUSE_CURSOR.DEFAULT,
      );
    }, 0);
    this.annotation.drawBeatIndicator({ beatTypeNumber: this.beatTypeNumber });
    this.markers.updateARBeatMode(true);
    this.markers.draw(this.offsetX, this.isInViewAllMode);
    this.propsFunction.handleOpenSnackbar(
      isDeleteBeatMode(this.beatTypeNumber)
        ? 'Delete beat'
        : `Add/Change ${convertBeatTypeNumberToText(this.beatTypeNumber)}`,
    );
    this.propsFunction.updateBeatTypeNumber(this.beatTypeNumber);
    this.propsFunction.updateARBeatModeStatus(true);
    window.addEventListener('keydown', this.eventARBeatMarker, true);
    if (this.annotation.selectedObj) {
      this.annotation.selectedObj.deselect();
      this.annotation.selectedObj = null;
    }
    if (this.ecgView.specifySampleMark || this.ecgView.specifyPattern) {
      this.ecgView.specifySampleMark = null;
      this.ecgView.specifyPattern = null;
    }
    this.renderECG();
  };

  AIECGChart.prototype.exitARBeatMode = function () {
    this.markers.deselectBeat();
    this.markers.resetHoverBeat();
    this.markers.updateARBeatMode(false);
    this.markers.draw(this.offsetX, this.isInViewAllMode);
    this.annotation.deleteBeatSelection();
    this.isDrawingBeatSelect = false;
    this.scrollDirection = null;
    this.pointOnMove = null;
    this.haveEntered = false;
    this.isScrollingChart = false;
    clearInterval(this.scrollInterval);
    this.propsFunction.updateSelectingBeatStatus(this.isDrawingBeatSelect);
    this.changeCursor(MOUSE_CURSOR.DEFAULT);
    this.actionType = null;
    this.isDownRight = false;
    this.isDownLeft = false;
    this.beatTypeNumber = null;
    this.propsFunction.updateBeatTypeNumber(this.beatTypeNumber);
    this.annotation.deleteBeatIndicator();
    this.renderECG();
    this.propsFunction.updateARBeatModeStatus(false);
    window.removeEventListener('keydown', this.eventARBeatMarker, true);
  };

  AIECGChart.prototype.startZoom = function (isZoomIn: any, staticValue: any) {
    if (!this.isDrawingBCC) {
      this.actionType = ACTION_TYPE.ZOOMING;
      this.isInWaitingZoomMode = true;

      window.addEventListener('keyup', this.eventCancelZoom);
    }

    if (this.zoomingChannel === null || typeof this.zoomingChannel === 'undefined') {
      this.beforeZoomValue = {
        speed: this.config.speed,
        gain: this.config.gain,
        displayedChannelsArr: this.displayedChannelsArr,
        numberOfVisibleChannels: this.config.numberOfVisibleChannels,
        someChannelsHidden: this.config.someChannelsHidden,
        sampleRange: this.config.sampleRange,
        samples: this.samples,
      };
      ECG_VIEWER_CONFIG_STORE.beforeZoomValue = this.beforeZoomValue;
    }

    if (this.annotation.selectedObj) {
      this.annotation.selectedObj.deselect();
      this.annotation.selectedObj = null;
    }

    if (this.ecgView.specifySampleMark || this.ecgView.specifyPattern) {
      this.ecgView.specifySampleMark = null;
      this.ecgView.specifyPattern = null;
    }

    // *: Set new speed & gain follow zoom condition
    if (staticValue) {
      this.config.newSpeed = staticValue.speed;
      this.config.newGain = staticValue.gain;
      this.actionType = null;

      return;
    }

    let speedIdx;
    let gainIdx;
    this.changeCursor(MOUSE_CURSOR.ZOOM_IN);

    // *: Zoom speed index
    for (let i = 0; i < SPEED_VALUES.length; i += 1) {
      if (this.config.speed === SPEED_VALUES[i]) {
        speedIdx = i;

        if (i <= SPEED_VALUES.length - 2) {
          speedIdx += 1;

          break;
        }
      }
    }

    // *: Zoom gain index
    for (let i = 0; i < GAIN_VALUES.length; i += 1) {
      if (this.config.gain === GAIN_VALUES[i]) {
        gainIdx = i;

        if (i <= GAIN_VALUES.length - 3) {
          gainIdx += 2;

          break;
        }
      }
    }

    this.config.newSpeed = SPEED_VALUES[speedIdx];
    this.config.newGain = GAIN_VALUES[gainIdx];
    window.addEventListener('keyup', this.eventCancelZoom);
    this.renderECG();
  };

  AIECGChart.prototype.zoom = function (channelIndex: any, x: any) {
    const { samplingRate, width } = this.config;

    if (this.annotation.selectedObj && this.annotation.selectedObj.type !== ANNOTATION_TYPE.BCC) {
      this.annotation.selectedObj.deselect();
      this.annotation.selectedObj = null;
    }

    this.changeCursor(MOUSE_CURSOR.DEFAULT);

    // TODO: Test 2 below code lines
    this.propsFunction.updateCompletedStatus(false, false);
    this.isDrawingText = false;

    this.isInWaitingZoomMode = false;
    this.isInZoomMode = true;
    ECG_VIEWER_CONFIG_STORE.isInZoomMode = this.isInZoomMode;
    this.config.numberOfVisibleChannels = this.rawDataZoom.length;
    this.lastPrevChanIdx = this.config.prevChanIdx;

    const xr = this.offsetX + Math.round((x * samplingRate * this.config.wndDuration) / width);
    const changeProportion = this.config.speed / this.config.newSpeed;
    this.config.speed = this.config.newSpeed;
    this.config.gain = this.config.newGain;
    this.config.wndDuration *= changeProportion;

    ECG_VIEWER_CONFIG_STORE.spdValue = this.config.speed;
    ECG_VIEWER_CONFIG_STORE.ampValue = this.config.gain;
    ECG_VIEWER_CONFIG_STORE.wndDuration = this.config.wndDuration;

    const halfWindow = Math.round(((width / 2) * samplingRate * this.config.wndDuration) / width);
    this.offsetX = xr > halfWindow ? xr - halfWindow : 0;

    if (this.zoomingChannel === null || typeof this.zoomingChannel === 'undefined') {
      this.zoomingChannel = channelIndex;
      ECG_VIEWER_CONFIG_STORE.zoomingChannel = this.zoomingChannel;
      this.displayedChannelsArr = [this.displayedChannelsArr[channelIndex]];
      ECG_VIEWER_CONFIG_STORE.visibleChannels = this.displayedChannelsArr;
    }

    this.config.prevChanIdx = this.zoomingChannel;

    this.config.someChannelsHidden = true;

    this.updateConfig();

    this.propsFunction.updateWaitingZoomModeStatus(false);
    this.propsFunction.updateZoomModeStatus(true);
    this.propsFunction.updateSpdMenu(this.config.speed);
    this.propsFunction.updateAmpMenu(this.config.gain);
    this.propsFunction.updateVisibleChannels(this.displayedChannelsArr, this.isDrawingBCC);
    this.propsFunction.updatePrevChanIdx(this.config.prevChanIdx);

    if (!this.isDrawingBCC) {
      window.removeEventListener('keyup', this.eventCancelZoom);
    }

    window.addEventListener('keyup', this.eventExitZoom);
  };

  AIECGChart.prototype.exitZoom = function () {
    if (this.annotation.selectedObj && this.annotation.selectedObj.type !== ANNOTATION_TYPE.BCC) {
      this.annotation.selectedObj.deselect();
      this.annotation.selectedObj = null;
    }

    this.propsFunction.updateCompletedStatus(false, false);

    if (this.isDrawingBCC) {
      this.isDrawingBCC = false;
    }

    if (this.zoomingChannel === null || typeof this.zoomingChannel === 'undefined') {
      this.changeCursor(MOUSE_CURSOR.DEFAULT);
      this.actionType = null;
      this.beforeZoomValue = null;
      ECG_VIEWER_CONFIG_STORE.beforeZoomValue = this.beforeZoomValue;

      this.isInWaitingZoomMode = false;
      this.propsFunction.updateZoomModeStatus(false);
      this.propsFunction.updateWaitingZoomModeStatus(false);
      this.propsFunction.updateCompletedStatus(false, false);
      window.removeEventListener('keyup', this.eventCancelZoom);
      return;
    }

    const { sampleRange } = this.config;

    this.offsetX = Math.round(
      this.offsetX - this.beforeZoomValue.sampleRange / 2 + sampleRange / 2,
    );

    this.zoomingChannel = null;
    ECG_VIEWER_CONFIG_STORE.zoomingChannel = null;

    const changeProportion = this.config.speed / this.beforeZoomValue.speed;

    this.config.speed = this.beforeZoomValue.speed;
    this.config.gain = this.beforeZoomValue.gain;
    this.config.wndDuration *= changeProportion;
    ECG_VIEWER_CONFIG_STORE.spdValue = this.config.speed;
    ECG_VIEWER_CONFIG_STORE.ampValue = this.config.gain;
    ECG_VIEWER_CONFIG_STORE.wndDuration = this.config.wndDuration;

    this.config.prevChanIdx = this.lastPrevChanIdx;
    this.config.numberOfVisibleChannels = this.beforeZoomValue.numberOfVisibleChannels;
    this.config.someChannelsHidden = this.beforeZoomValue.someChannelsHidden;
    this.displayedChannelsArr = this.beforeZoomValue.displayedChannelsArr;
    ECG_VIEWER_CONFIG_STORE.visibleChannels = this.displayedChannelsArr;

    this.beforeZoomValue = null;
    ECG_VIEWER_CONFIG_STORE.beforeZoomValue = this.beforeZoomValue;

    this.sampleRangeZoom = null;
    this.isInZoomMode = false;
    ECG_VIEWER_CONFIG_STORE.isInZoomMode = this.isInZoomMode;

    this.propsFunction.updateZoomModeStatus(false);
    this.propsFunction.updateWaitingZoomModeStatus(false);
    this.propsFunction.updateSpdMenu(this.config.speed);
    this.propsFunction.updateAmpMenu(this.config.gain);
    this.propsFunction.updateVisibleChannels(this.displayedChannelsArr);
    this.propsFunction.updatePrevChanIdx(this.config.prevChanIdx);

    this.filterSamples(true);
    this.calculatePixelSamplesFromSamples(this.samples);
    this.updateConfig();

    window.removeEventListener('keyup', this.eventExitZoom);
  };

  AIECGChart.prototype.enterViewAllMode = function () {
    if (this.annotation.selectedObj) {
      this.annotation.selectedObj.deselect();
      this.annotation.selectedObj = null;
    }

    if (this.ecgView.specifySampleMark || this.ecgView.specifyPattern) {
      this.ecgView.specifySampleMark = null;
      this.ecgView.specifyPattern = null;
    }
    this.propsFunction.handleOpenSnackbar('View all strip');
    this.isInViewAllMode = true;
    ECG_VIEWER_CONFIG_STORE.isInViewAllMode = this.isInViewAllMode;

    this.beforeViewAllValue = {
      speed: this.config.speed,
      wndDuration: this.config.wndDuration,
      gain: this.config.gain,
    };
    ECG_VIEWER_CONFIG_STORE.beforeViewAllValue = this.beforeViewAllValue;

    this.config.speed = this.config.defaultSpeed / this.viewAllProportion;
    this.config.wndDuration = this.config.defaultWndDuration * this.viewAllProportion;
    ECG_VIEWER_CONFIG_STORE.spdValue = this.config.speed;
    ECG_VIEWER_CONFIG_STORE.wndDuration = this.config.wndDuration;

    this.propsFunction.updateSpdMenu(this.config.speed);
    this.canvasBackground.clear();
    this.viewAll();
  };

  AIECGChart.prototype.exitViewAllMode = function () {
    if (this.annotation.selectedObj && this.annotation.selectedObj.type !== ANNOTATION_TYPE.BCC) {
      this.annotation.selectedObj.deselect();
      this.annotation.selectedObj = null;
    }

    this.config.speed = DEFAULT_ECG_VIEWER_CONFIG.spdValue;
    this.config.wndDuration = DEFAULT_ECG_VIEWER_CONFIG.wndDuration;
    ECG_VIEWER_CONFIG_STORE.spdValue = this.config.speed;
    ECG_VIEWER_CONFIG_STORE.wndDuration = this.config.wndDuration;

    this.isInViewAllMode = false;
    ECG_VIEWER_CONFIG_STORE.isInViewAllMode = this.isInViewAllMode;

    this.beforeViewAllValue = null;
    ECG_VIEWER_CONFIG_STORE.beforeViewAllValue = this.beforeViewAllValue;

    this.propsFunction.updateSpdMenu(this.config.speed);
    this.viewAll();
  };

  AIECGChart.prototype.viewAll = function () {
    // *: Re-draw Event Position
    this.updateConfig();
    const arrEventPosition = _.filter(
      this.annotation.annoArr,
      (x: any) => x.type === ANNOTATION_TYPE.EVENT_POSITION,
    );
    if (arrEventPosition.length > 0) {
      _.forEach(arrEventPosition, (x: any) => {
        this.canvasObject.remove(x.rect, x.text, x.bottomRect);
      });
      _.remove(this.annotation.annoArr, (x: any) => x.type === ANNOTATION_TYPE.EVENT_POSITION);
      this.drawEventsPosition(this.events, false);
    }
  };

  AIECGChart.prototype.exportAll = function (
    preventExportAllStripSvg: any = false,
    isShowEvent: any,
  ) {
    // * Set original data to current data
    this.originalBeatMarkers = [...this.currentBeatMarkers];
    this.originalHesBeatStatus = [...this.currentHesBeatStatus];

    this.isExporting = true;

    const {
      speed,
      exportSpeed,
      gain,
      exportGain,
      wndDuration,
      exportWndDuration,
      majorDivSize,
      exportMajorDivSize,
      fineGrid,
      exportFineGrid,
      numberOfVisibleChannels,
      exportNumberOfVisibleChannels,
      isShowBeatMarkers,
    } = this.config;

    const currentSpeed = speed;
    const currentGain = gain;
    const currentWndDuration = wndDuration;
    const currentMajorDivSize = majorDivSize;
    const currentFineGrid = fineGrid;
    const currentNumberOfVisibleChannels = numberOfVisibleChannels;
    const currentDisplayedChannelsArr = this.displayedChannelsArr;

    let setConfigObject = {
      speedSetConfig: this.config.defaultSpeed / this.viewAllProportion,
      gainSetConfig: exportGain,
      wndDurationSetConfig: this.config.defaultWndDuration * this.viewAllProportion,
      majorDivSizeSetConfig: exportMajorDivSize,
      fineGridSetConfig: exportFineGrid,
      numberOfVisibleChannelsSetConfig: exportNumberOfVisibleChannels,
      displayedChannelsArrSetConfig: this.exportDisplayedChannelsArr,
    };

    let allStripSvg = [];
    if (!preventExportAllStripSvg) {
      // *: Set config
      this.setConfig(setConfigObject);

      // hide beat normal
      this.showHideBeatMarkers(false);
      // hide grid normal
      this.exportGrid.showHideGrid(false);

      // *: Export 60s (like View All mode, svg tab)
      allStripSvg = this.ecgView.exportAll({
        titleChart: this.titleChart,
        markers: this.markers,
        annotation: this.annotation,
        gridBackGround: this.exportGrid,
        canvasObject: this.canvasObject,
        isShowEvent,
      });
    }

    this.showHideBeatMarkers(isShowBeatMarkers);
    this.exportGrid.drawExportGrid();
    this.exportGrid.showHideGrid(true);

    setConfigObject = {
      speedSetConfig: exportSpeed,
      gainSetConfig: currentGain,
      wndDurationSetConfig: exportWndDuration,
      majorDivSizeSetConfig: exportMajorDivSize,
      // speedSetConfig: this.isInViewAllMode ? exportSpeed : currentSpeed,
      // gainSetConfig: this.isInViewAllMode ? exportGain : currentGain,
      // wndDurationSetConfig: this.isInViewAllMode ? exportWndDuration : currentWndDuration,
      // majorDivSizeSetConfig: this.isInViewAllMode ? exportMajorDivSize : currentMajorDivSize,
      fineGridSetConfig: exportFineGrid,
      numberOfVisibleChannelsSetConfig: exportNumberOfVisibleChannels,
      displayedChannelsArrSetConfig: this.exportDisplayedChannelsArr,
    };

    // *: Set config
    this.setConfig(setConfigObject);

    // *: Export all (svg tag)
    const allIndexStripSvg = this.ecgView.exportAll({
      titleChart: this.titleChart,
      markers: this.markers,
      annotation: this.annotation,
      gridBackGround: this.exportGrid,
      canvasObject: this.canvasObject,
      isShowEvent,
    });

    this.isExporting = false;

    setConfigObject = {
      speedSetConfig: currentSpeed,
      gainSetConfig: currentGain,
      wndDurationSetConfig: currentWndDuration,
      majorDivSizeSetConfig: currentMajorDivSize,
      fineGridSetConfig: currentFineGrid,
      numberOfVisibleChannelsSetConfig: currentNumberOfVisibleChannels,
      displayedChannelsArrSetConfig: currentDisplayedChannelsArr,
    };

    // *: Reset config
    this.setConfig(setConfigObject);

    for (let i = 0; i < allIndexStripSvg.length; i += 1) {
      allIndexStripSvg[i] = optimizeSVG(allIndexStripSvg[i]);
    }

    for (let i = 0; i < allStripSvg.length; i += 1) {
      allStripSvg[i] = optimizeSVG(allStripSvg[i]);
    }

    // let timeOut = 0;
    // _.forEach(allStripSvg, (element: any, index: any) => {
    //   setTimeout(() => {
    //     const blob = new Blob([optimizeSVG(element)], { type: 'image/svg+xml; charset=utf-8' });
    //     FileSaver.saveAs(blob, `ECG-${index + 1}.svg`, true);
    //   }, timeOut);
    //   timeOut += 100;
    // });

    return { allIndexStripSvg, allStripSvg };
  };

  AIECGChart.prototype.calculateBeatTypeV = function () {
    const beatTypeVArray: any[] = [];
    this.aiData.hesBeatStatus.forEach((beatStatus: any, index: any) => {
      if (beatStatus === 60) {
        const { indexToPix, secondsToPix } = this.config;
        const time = (this.aiData.beats[index] * indexToPix) / secondsToPix;
        const timeString = this.titleChart.getTimeInfo(time);
        beatTypeVArray.push({ startBeatTypeVSample: this.aiData.beats[index], timeString });
      }
    });
    return beatTypeVArray;
  };

  AIECGChart.prototype.calculateBeatTypeS = function () {
    const beatTypeSArray: any[] = [];
    this.aiData.hesBeatStatus.forEach((beatStatus: any, index: any) => {
      if (beatStatus === 70) {
        const { indexToPix, secondsToPix } = this.config;
        const time = (this.aiData.beats[index] * indexToPix) / secondsToPix;
        const timeString = this.titleChart.getTimeInfo(time);
        beatTypeSArray.push({ startBeatTypeSSample: this.aiData.beats[index], timeString });
      }
    });
    return beatTypeSArray;
  };

  AIECGChart.prototype.calculateEventArray = function (
    withTime: any,
    channelIndex: any,
    isNoise: any,
  ) {
    const eventArray: any[] = [];
    let startSample: number | undefined;
    let endSample: number | undefined;
    const code = isNoise ? 0x0001 : 0x0800;

    // Bitmask flag checks against hesEventStatus - genuine bitwise use, not a mistaken logical operator.
    /* eslint-disable no-bitwise */
    this.aiData.hesEventStatus.forEach((element: any, index: any) => {
      if (
        (element & code) === code &&
        (this.aiData.hesEventStatus[index - 1] & code) !== code &&
        (this.aiData.hesEventStatus[index + 1] & code) === code
      ) {
        startSample = this.aiData.beats[index];
      }

      if (
        (element & code) === code &&
        (this.aiData.hesEventStatus[index + 1] & code) !== code &&
        (this.aiData.hesEventStatus[index - 1] & code) === code
      ) {
        endSample = this.aiData.beats[index];
      }
      /* eslint-enable no-bitwise */

      if (startSample && endSample) {
        const { sampleLength } = this.config;
        startSample = startSample < 0 ? 0 : startSample;
        endSample = endSample > sampleLength - 1 ? sampleLength - 1 : endSample;
        if (withTime) {
          const { indexToPix, secondsToPix } = this.config;
          const time = (startSample * indexToPix) / secondsToPix;
          const timeString = this.titleChart.getTimeInfo(time);
          eventArray.push({
            startSample,
            endSample,
            channelIndex,
            timeString,
          });
        } else if (isNoise) {
          eventArray.push({ startSample, endSample });
        } else {
          eventArray.push({ startSample, endSample, channelIndex });
        }
        startSample = undefined;
        endSample = undefined;
      }
    });
    return eventArray;
  };

  AIECGChart.prototype.applyAI = function (
    isAppliedAI: any,
    isAIBeatMarkers: any,
    aiChannelIndex: any,
    isShowVES: any,
    isShowSVES: any,
  ) {
    this.isAppliedAI = isAppliedAI;
    this.isShowVES = isShowVES;
    this.isShowSVES = isShowSVES;

    const afibEventsArray = this.calculateEventArray(false, aiChannelIndex);
    // const noiseEventsArray = this.calculateEventArray(false, aiChannelIndex, true);
    this.ecgView.applyAI(isAppliedAI, this.offsetX, afibEventsArray);
    // this.annotation.drawNoiseEvent(noiseEventsArray, this.offsetX, isAppliedAI);

    let willApplyBeatMarkers;
    let willApplyHesBeatStatus;
    if (aiChannelIndex === this.diagnosticLead - 1) {
      // * if isAIBeatMarkers: originalBeatMarkers belong to AI
      // * else: originalBeatMarkers belong to normal
      if (isAIBeatMarkers) {
        if (isAppliedAI) {
          willApplyBeatMarkers = this.originalBeatMarkers;
          willApplyHesBeatStatus = this.originalHesBeatStatus;
        } else {
          willApplyBeatMarkers = this.pantompkinBeatMarkers;
          willApplyHesBeatStatus = [];
        }
      } else if (isAppliedAI) {
        willApplyBeatMarkers = this.aiData.beats;
        willApplyHesBeatStatus = this.aiData.hesBeatStatus;
      } else {
        willApplyBeatMarkers =
          this.originalBeatMarkers.length > 0
            ? this.originalBeatMarkers
            : this.pantompkinBeatMarkers;
        willApplyHesBeatStatus = [];
      }
    } else if (isAppliedAI) {
      willApplyBeatMarkers = this.aiData.beats;
      willApplyHesBeatStatus = this.aiData.hesBeatStatus;
    } else {
      willApplyBeatMarkers = this.pantompkinBeatMarkers;
      willApplyHesBeatStatus = [];
    }
    this.currentBeatMarkers = willApplyBeatMarkers;
    this.currentHesBeatStatus = willApplyHesBeatStatus;
    this.markers.applyAI(
      isAppliedAI,
      this.offsetX,
      this.isInViewAllMode,
      willApplyBeatMarkers,
      willApplyHesBeatStatus,
      isShowVES,
      isShowSVES,
      this.aiData,
    );

    this.updateHorizCaliperBelowText();
    this.renderECG();
  };

  AIECGChart.prototype.handleChangeDiagnosisLead = function (
    channelAIData: any,
    aiChannelIndex: any,
    isAIBeatMarkers: any,
    isShowVES: any,
    isShowSVES: any,
  ) {
    this.aiData = channelAIData;
    // *: Recalculate ai data
    this.applyAI(this.isAppliedAI, isAIBeatMarkers, aiChannelIndex, isShowVES, isShowSVES);
  };

  AIECGChart.prototype.updateAIData = function (channelAIData: any) {
    this.aiData = channelAIData;
  };

  AIECGChart.prototype.calculateAIHeartRate = function () {
    return this.markers.calculateAIHeartRate();
  };

  AIECGChart.prototype.calculateBeatTypeValue = function () {
    const beatTypeVArray = this.calculateBeatTypeV();
    const beatTypeSArray = this.calculateBeatTypeS();
    const afibEventArray = this.calculateEventArray(true);
    return { beatTypeVArray, beatTypeSArray, afibEventArray };
  };

  AIECGChart.prototype.handleChangeBeatType = function (startSample: any) {
    const { maxIndex, minIndex } = this.config;
    this.offsetX = startSample;
    this.offsetX = this.offsetX < maxIndex ? this.offsetX : maxIndex;
    this.offsetX = this.offsetX > minIndex ? this.offsetX : minIndex;
    this.annotation.scroll(this.offsetX, this.displayedChannelsArr);
    this.loadECG(this.offsetX);
  };

  AIECGChart.prototype.updateHorizCaliperBelowText = function () {
    if (this.annotation.annoArr && this.annotation.annoArr.length) {
      const beatMarkersUpdate = this.annotation.annoArr.filter(
        (x: any) => x.type === 'HorizontalCaliper',
      );
      if (beatMarkersUpdate && beatMarkersUpdate.length) {
        beatMarkersUpdate.forEach((beatMarkerUpdate: any) => {
          beatMarkerUpdate.updateBelowText(this.markers.getBeatMarkers(), this.offsetX);
        });
      }
    }
  };

  AIECGChart.prototype.pickDate = function () {
    if (this.propsFunction.handlePickDate) {
      if (this.clickedOffsetX <= 0) return;
      const diffSeconds = this.clickedOffsetX / this.config.samplingRate;
      this.propsFunction.handlePickDate(diffSeconds);
    }
  };

  AIECGChart.prototype.showEventIndicator = function (pos: any, text: any) {
    this.annotation.drawEventIndicator(pos, this.offsetX, text);
  };

  AIECGChart.prototype.showBookmarkIndicators = function (listBookmarks: any) {
    this.annotation.drawBookmarkIndicators(listBookmarks, this.offsetX);
  };

  AIECGChart.prototype.getRemainSeconds = function () {
    const { sampleLength } = this.config;
    const remainSamples = sampleLength - this.offsetX;
    const remainSeconds = remainSamples / this.config.samplingRate;
    return remainSeconds;
  };

  AIECGChart.prototype.handleCapture = function (
    captureDuration: any,
    highlightChannel: any,
    isShowEvent: any,
  ) {
    const { samplingRate } = this.config;

    const sampleMarkRange = samplingRate * captureDuration;
    const sampleMark = {
      channelIndex: highlightChannel,
      startSample: this.offsetX,
      endSample: this.offsetX + sampleMarkRange - 1,
      sampleMarkRange,
    };

    this.ecgView.sampleMarksArray.length = 0;

    this.ecgView.drawSampleMark(this.offsetX, sampleMark);
    this.propsFunction.updateCompletedStatus(false, true);

    const { allIndexStripSvg } = this.exportAll(true, isShowEvent);

    const widgets = this.getWidgets();
    const PERMITTED_ANNOTATION = [
      ANNOTATION_TYPE.HLINE,
      ANNOTATION_TYPE.VLINE,
      ANNOTATION_TYPE.TEXT,
      ANNOTATION_TYPE.BCC,
      ANNOTATION_TYPE.SAMPLE_MARK,
      ANNOTATION_TYPE.PATTERN,
    ];
    const sampleMarkWidgets = widgets.filter((x: any) => PERMITTED_ANNOTATION.includes(x.type));
    return {
      draw: sampleMarkWidgets,
      allIndexStripSvg,
    };
  };

  AIECGChart.prototype.calculateRangeData = function (
    type: any,
    captureDuration: any,
    ecgDataMap: any,
    startMoment: any,
    timezoneOffset: any,
  ) {
    const { samplingRate } = this.config;
    const sampleMarkRange = samplingRate * captureDuration;
    const totalSeconds = sampleMarkRange / ecgDataMap.samplingFrequency;
    const startSeconds =
      type === TYPE_MODE.ADD_BEAT
        ? this.clickedOffsetX / ecgDataMap.samplingFrequency
        : this.offsetX / ecgDataMap.samplingFrequency;
    const startTime = convertToMomentJs(startMoment, timezoneOffset)
      .add(startSeconds, 'seconds')
      ?.toISOString(); // Updated to use moment
    const endTime = convertToMomentJs(startMoment, timezoneOffset)
      .add(startSeconds + totalSeconds, 'seconds')
      ?.toISOString(); // Updated to use moment
    const rangesWithin = getDataRangeWithin(ecgDataMap, moment(startTime), moment(endTime)); // Updated to use moment
    return rangesWithin.ranges.length > 0;
  };

  AIECGChart.prototype.getOffsetX = function () {
    return this.offsetX;
  };

  AIECGChart.prototype.getJSONObject = function () {
    const { samplingRate, adcZero, adcGain, LFF, HFF, prevChanIdx } = this.config;

    const { speed, gain } = this.beforeZoomValue
      ? this.beforeZoomValue
      : this.beforeViewAllValue
        ? this.beforeViewAllValue
        : this.config;

    const displayedChannelsArr = this.beforeZoomValue
      ? this.beforeZoomValue.displayedChannelsArr
      : this.displayedChannelsArr;

    const annoArr = this.annotation.getJSONArr();
    const viewArr = this.ecgView.getJSONObject();

    const result = {
      object: [...annoArr, ...viewArr],
      dataECG: {
        samples: this.rawDataFromNetwork,
        samplingRate,
        offsetX: this.offsetX,
        adcZero,
        adcGain,
        speed,
        gain,
        LFF,
        HFF,
        prevChanIdx,
        displayedChannelsArr,
      },
    };

    result.object.push(this.markers.getJSONObject());

    return JSON.stringify(result);
  };
})();

export { AIECGChart };
export default { AIECGChart };
